import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";
// mui
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
//
import Divider from "@mui/material/Divider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
// lib
import usePrevious from "components/Calculators/components/utils/usePrevious";
import loadCalculation from "components/Calculators/components/utils/loadCalculation";
import InterestRateChart from "components/InterestRateChart";
import formatMoney from "lib/formatMoney";
import sleep from "lib/sleep";
// components
import QuestionRenderer from "components/Calculators/components/QuestionRenderer";
import CalculatorQuestions from "components/Calculators/DSCRCalculator/components/CalculatorQuestions";
import getDSCRLTVTiers from "components/Loans/utils/getDSCRLTVTiers";
import getDSCRPrepaymentOptions from "components/Loans/utils/getDSCRPrepaymentOptions";
import getDSCRBuydownOptions from "components/Loans/utils/getDSCRBuydownOptions";
import Authenticator from "components/Authenticator";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);
import SaveSharePDF from "components/Calculators/components/SaveSharePDF";
import calculateMonthlyPmtAndDSCR from "components/Calculators/DSCRCalculator/utils/calculateMonthlyPmtAndDSCR";
import LTVQuoteTable from "components/Calculators/DSCRCalculator/components/LTVQuoteTable";
import getOriginationFee from "components/Loans/utils/getOriginationFee";

export default function DSCRCalculator({
  URL,
  CapitalData,
  externalCalc,
  previousCalc,
  externalShowMeMyRate,
  plugInTheNumbers,
  externalExampleCalc,
  calcs,
  loadCalcs,
  refCalcs,
  calculatorMode, // 'DSCR' || 'DSCR loan'
  loanTermsUI = false,
  loan,
  onVersionSelect,
}) {
  const theme = useTheme();
  const router = useRouter();

  const calcType = calculatorMode;

  const {
    isMobile,
    user,
    userProfile,
    setSnackbarData,
    userPermissions,
    userRoles,
  } = useStore(AppStore);

  const isOMTeam =
    userRoles.length > 0 &&
    userPermissions.Role &&
    [userRoles[0], userRoles[1]].includes(userPermissions.Role) &&
    ["OM"].includes(userPermissions.Organization);

  const [versionDifferences, setVersionDifferences] = useState({});

  const [loanQuoteData, setLoanQuoteData] = useState({});

  const [externalCaclObjData, setExternalCalcObjData] = useState(
    externalExampleCalc && Object.keys(externalExampleCalc).length > 0
      ? externalExampleCalc
      : {
          "Credit score": 681,
          "HOA dues": 211,
          Insurance: 1254,
          "Purchase price": 143000,
          "Monthly rent": 1450,
          "Property taxes": 988,
        }
  ); // this is default data for dscr-loan page where this custom obj isn't provided

  const [showQuestionDescription, setShowQuestionDescription] = useState({});
  // Add originalValueRef to track original values
  const originalValueRef = useRef(
    externalExampleCalc && Object.keys(externalExampleCalc).length > 0
      ? {
          "Credit score": externalExampleCalc.creditScore,
          "HOA dues": externalExampleCalc.hoa,
          Insurance: externalExampleCalc.insurance,
          "Purchase price": externalExampleCalc.price,
          "Monthly rent": externalExampleCalc.rent,
          "Property taxes": externalExampleCalc.taxes,
        }
      : {
          "Credit score": 681,
          "HOA dues": 211,
          Insurance: 1254,
          "Purchase price": 143000,
          "Monthly rent": 1450,
          "Property taxes": 988,
        }
  );
  // Consolidated original values ref (combines trueOriginalValueRef + originalCalculatedValuesRef)
  const originalValuesRef = useRef({});

  // Consolidated value changes state (combines valueChanges + calculatedValueChanges)
  const [valueChanges, setValueChanges] = useState({});

  // Track if user has started interacting in loanTermsUI mode (to switch from version diffs to user diffs)
  const hasUserInteractedInLoanTermsUIRef = useRef(false);

  // Store initial load values for loanTermsUI mode (before any user interaction)
  const initialLoadValuesRef = useRef({});

  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({});
  const [loanPurpose, setLoanPurpose] = useState({});
  const [loanAmount, setLoanAmount] = useState({});
  const [monthlyPmt, setMonthlyPmt] = useState({});
  const [monthlyPitia, setMonthlyPitia] = useState(0);
  const [monthlyRent, setMonthlyRent] = useState(0);
  const [annualRent, setAnnualRent] = useState(0);
  const [annualTaxes, setAnnualTaxes] = useState(0);
  const [annualInsurance, setAnnualInsurance] = useState(0);
  const [annualHOA, setAnnualHOA] = useState(0);
  const [propertyValue, setPropertyValue] = useState(0);
  const [localInterestRate, setLocalInterestRate] = useState(0);
  const [LTV, setLTV] = useState(0);
  const [annualPropertyManagementExpense, setAnnualPropertyManagementExpense] =
    useState(0);
  const [dscr, setDSCR] = useState(0);
  const [cashToClose, setCashToClose] = useState(0);
  const [cashToCloseIsPositive, setCashToCloseIsPositive] = useState(0);
  const [liquidityToVerify, setLiquidityToVerify] = useState(0);
  const [pitiaReserveMonths, setPitiaReserveMonths] = useState(6);
  const [variableExpenseCashToClose, setVariableExpenseCashToClose] =
    useState(0);
  const [monthlyCashflow, setMonthlyCashflow] = useState(0);
  const [loanFees, setLoanFees] = useState(0);
  const [titleFees, setTitleFees] = useState(0);
  const [escrow, setEscrow] = useState(0);
  const [prepaidInterest, setPrepaidInterest] = useState(0);
  const [prepaidInsurance, setPrepaidInsurance] = useState(0);
  const [originationFee, setOriginationFee] = useState(0);
  const [originationFeeExplanation, setOriginationFeeExplanation] =
    useState("");

  const [numberOfProperties, setNumberOfProperties] = useState(0);
  const [buydownAmount, setBuydownAmount] = useState(0);
  const [buydownSpread, setBuydownSpread] = useState(0);
  const [paymentReserve, setPaymentReserve] = useState(0);
  const [estimatedAsIsValue, setEstimatedAsIsValue] = useState(0);
  const [existingDebt, setExistingDebt] = useState(0);
  const [showSaveFields, setShowSaveFields] = useState(false);
  const [changedKey, setChangedKey] = useState("");
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [suggestionChoice, setSuggestionChoice] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  // const [showSaved, setShowSaved] = useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);
  // DSCR
  // const [DSCRRateRange, setDSCRRateRange] = useState([]);
  const [current5yrTreasury, setCurrent5yrTreasury] = useState(null);
  const [personalizedIntSpread, setPersonalizedIntSpread] = useState(null);
  const [DSCRCutoff, setDSCRCutoff] = useState(null);
  const [creditScoreCutoff, setCreditScoreCutoff] = useState(null);
  const [creditScore, setCreditScore] = useState(null);
  const [prepaymentOptions, setPrepaymentOptions] = useState({});
  const [buydownOptions, setBuydownOptions] = useState({});
  const [rateUsed, setRateUsed] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  // Add state for tracking all descriptions visibility
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  // State for closing costs collapse
  const [showClosingCostsCollapse, setShowClosingCostsCollapse] =
    useState(true);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

  // Add state to track calculated value differences for loan terms UI
  const [calculatedValueDifferences, setCalculatedValueDifferences] = useState(
    {}
  );

  // Add state to track if user has made changes in loanTermsUI mode
  const [
    userHasChangedValuesInLoanTermsUI,
    setUserHasChangedValuesInLoanTermsUI,
  ] = useState(false);

  // Debouncing for change detection
  const debounceTimeoutRef = useRef({});

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeoutRef.current).forEach(clearTimeout);
    };
  }, []);

  // Function to compare two versions and find differences
  const compareVersions = (currentVersion, previousVersion) => {
    const differences = {};

    if (!currentVersion || !previousVersion) return differences;

    // Compare answers between versions
    currentVersion.Answers.forEach((currentAnswer) => {
      const previousAnswer = previousVersion.Answers.find(
        (prev) => prev.q_title === currentAnswer.q_title
      );

      if (
        previousAnswer &&
        currentAnswer.a_string !== previousAnswer.a_string
      ) {
        differences[currentAnswer.q_title] = {
          current: currentAnswer.a_string,
          previous: previousAnswer.a_string,
          difference:
            parseFloat(currentAnswer.a_string) -
            parseFloat(previousAnswer.a_string),
        };
      }
    });

    return differences;
  };

  // Function to compare calculated values between versions
  const compareCalculatedValues = (currentVersion, previousVersion) => {
    if (!currentVersion || !previousVersion) return {};

    const calculatedDifferences = {};

    // Calculate DSCR for both versions
    const getCurrentDSCR = (version) => {
      const monthlyRent =
        version.Answers.find((a) => a.q_title === "Monthly rent")?.a_string ||
        0;
      const annualTaxes =
        version.Answers.find((a) => a.q_title === "Annual taxes")?.a_string ||
        0;
      const annualInsurance =
        version.Answers.find((a) => a.q_title === "Annual insurance")
          ?.a_string || 0;
      const annualHOA =
        version.Answers.find((a) => a.q_title === "Annual HOA")?.a_string || 0;
      const monthlyPmt =
        version.Answers.find((a) => a.q_title === "Monthly payment")
          ?.a_string || 0;

      const annualRent = monthlyRent * 12;
      const totalAnnualExpenses =
        monthlyPmt * 12 +
        parseInt(annualTaxes) +
        parseInt(annualInsurance) +
        parseInt(annualHOA);

      return totalAnnualExpenses > 0 ? annualRent / totalAnnualExpenses : 0;
    };

    const getCurrentMonthlyCashflow = (version) => {
      const monthlyRent =
        version.Answers.find((a) => a.q_title === "Monthly rent")?.a_string ||
        0;
      const annualTaxes =
        version.Answers.find((a) => a.q_title === "Annual taxes")?.a_string ||
        0;
      const annualInsurance =
        version.Answers.find((a) => a.q_title === "Annual insurance")
          ?.a_string || 0;
      const annualHOA =
        version.Answers.find((a) => a.q_title === "Annual HOA")?.a_string || 0;
      const monthlyPmt =
        version.Answers.find((a) => a.q_title === "Monthly payment")
          ?.a_string || 0;

      return (
        parseInt(monthlyRent) -
        (parseInt(annualTaxes) / 12 +
          parseInt(annualInsurance) / 12 +
          parseInt(monthlyPmt) +
          parseInt(annualHOA) / 12)
      );
    };

    const getCurrentCashToClose = (version) => {
      return (
        version.Answers.find((a) => a.q_title === "Cash to close")?.a_string ||
        0
      );
    };

    const getCurrentLiquidityToVerify = (version) => {
      return (
        version.Answers.find((a) => a.q_title === "Liquidity to verify")
          ?.a_string || 0
      );
    };

    const currentDSCR = getCurrentDSCR(currentVersion);
    const previousDSCR = getCurrentDSCR(previousVersion);

    const currentMonthlyCashflow = getCurrentMonthlyCashflow(currentVersion);
    const previousMonthlyCashflow = getCurrentMonthlyCashflow(previousVersion);

    const currentCashToClose = getCurrentCashToClose(currentVersion);
    const previousCashToClose = getCurrentCashToClose(previousVersion);

    const currentLiquidityToVerify =
      getCurrentLiquidityToVerify(currentVersion);
    const previousLiquidityToVerify =
      getCurrentLiquidityToVerify(previousVersion);

    if (currentDSCR !== previousDSCR) {
      calculatedDifferences.dscr = {
        current: currentDSCR,
        previous: previousDSCR,
        difference: currentDSCR - previousDSCR,
      };
    }

    if (currentMonthlyCashflow !== previousMonthlyCashflow) {
      calculatedDifferences.monthlyCashflow = {
        current: currentMonthlyCashflow,
        previous: previousMonthlyCashflow,
        difference: currentMonthlyCashflow - previousMonthlyCashflow,
      };
    }

    if (currentCashToClose !== previousCashToClose) {
      calculatedDifferences.cashToClose = {
        current: currentCashToClose,
        previous: previousCashToClose,
        difference: currentCashToClose - previousCashToClose,
      };
    }

    if (currentLiquidityToVerify !== previousLiquidityToVerify) {
      calculatedDifferences.liquidityToVerify = {
        current: currentLiquidityToVerify,
        previous: previousLiquidityToVerify,
        difference: currentLiquidityToVerify - previousLiquidityToVerify,
      };
    }

    return calculatedDifferences;
  };

  // clean up if loan purpose is changed
  // everything other then below keys
  let preserveKeys = [
    "Monthly rent",
    "Annual taxes",
    "Annual insurance",
    "Annual HOA",
    "Loan purpose",
    "Calculation name",
    "Address (optional)",
    "Loan amortization",
  ];
  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = [
    "Loan amortization",
    "Monthly rent",
    "Annual taxes",
    "Annual insurance",
    "Annual HOA",
    "Loan purpose",
    "Purchase price",
    "Loan amount",
    "Estimated AS-IS value",
    "Existing debt",
    "Loan to value (LTV)",
    ...(loanTermsUI
      ? [
          "Credit spread",
          "Prepayment penalty",
          "Prepayment spread",
          "Buydown points",
          "Buydown amount",
          "Buydown spread",
          "5 year treasury",
        ]
      : []),
    "Interest rate",
  ];

  const secondPartOfQuestions = [
    "Origination fee",
    "Loan fees",
    "Title fees",
    "Escrow",
    "Prepaid interest",
    "Prepaid insurance",
    "Payment reserve",
  ];

  const thirdPart = ["Calculation name", "Address (optional)"];

  const showMeMyRatePart = ["Credit score"];

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };
  // Save the previous state
  const previousValues = usePrevious(currentValues);

  // TODO review necessity
  const defaultValues = {
    //   "Loan purpose": "purchase",
  };

  useEffect(() => {
    loadCalculation(
      defaultValues,
      externalCalc,
      externalExampleCalc,
      CalculatorQuestions,
      setQuestions,
      setAnswers,
      setCurrentValues,
      setShowUpdateFields,
      setNumberOfProperties
    );
  }, []);

  // Update differences when versions change or on initial load
  useEffect(() => {
    if (loanTermsUI && externalCalc && previousCalc) {
      const differences = compareVersions(externalCalc, previousCalc);

      setVersionDifferences(differences);

      const calculatedDifferences = compareCalculatedValues(
        externalCalc,
        previousCalc
      );

      setCalculatedValueDifferences(calculatedDifferences);

      // Reset the user interaction flag when switching versions to show version diffs

      setUserHasChangedValuesInLoanTermsUI(false);
    }
  }, [loanTermsUI, externalCalc, previousCalc]);

  // Run calculations on initial load to display calculated values with live treasury data
  useEffect(() => {
    if (answers.length > 0 && questions.length > 0) {
      // FIRST: Capture original values before any calculations
      answers.forEach((answer) => {
        if (!originalValuesRef.current[answer.q_title]) {
          originalValuesRef.current[answer.q_title] = answer.a_string || "";
        }
      });

      // THEN: Trigger calculations with live treasury data if available to update dependent fields
      const treasuryAnswer = answers.find(
        (a) => a.q_title === "5 year treasury"
      );

      if (treasuryAnswer && loanQuoteData?.treasury5yr?.rate) {
        // Use live API treasury data to trigger cascade: treasury → interest rate → payment → DSCR → cashflow

        handleUserSelection(
          loanQuoteData.treasury5yr.rate.toString(),
          treasuryAnswer,
          answers,
          questions,
          setAnswers
        );
      } else {
        // Fallback: Find any answer to trigger basic calculations
        const dummyAnswer =
          answers.find((a) => a.q_title === "Monthly rent") || answers[0];

        if (dummyAnswer) {
          handleUserSelection(
            dummyAnswer.a_string || "0",
            dummyAnswer,
            answers,
            questions,
            setAnswers
          );
        }
      }
    }
  }, [answers.length, questions.length, loanQuoteData?.treasury5yr?.rate]); // Include treasury rate in dependencies

  useEffect(() => {
    if (externalShowMeMyRate !== false && externalShowMeMyRate) {
      setShowMeMyRate(externalShowMeMyRate);
    }

    if (externalShowMeMyRate === false) {
      setShowMeMyRate(externalShowMeMyRate);
    }
  }, [externalShowMeMyRate]);

  useEffect(() => {
    if (plugInTheNumbers) {
      const updatedAnswers = answers.slice(0);

      const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
        return answer?.q_title === "Loan purpose";
      });

      if (!toUpdateAnswer.a_string) {
        // just default to purchase for ease for now
        toUpdateAnswer.a_string = "Purchase";
      }

      // Credit score from example harcoded at 681
      const [toUpdateAnswerCreditScore] = updatedAnswers.filter((answer) => {
        return answer?.q_title === "Credit score";
      });

      toUpdateAnswerCreditScore.a_string = externalCaclObjData.creditScore;

      setAnswers(updatedAnswers);

      // now get the tiers, no longer need LTV, will just show all LTVs even if its set
      // to show wider range of options
      (async () => {
        await getDSCRLTVTiers(updatedAnswers, setLoanQuoteData);
      })();
    }
  }, [plugInTheNumbers]);

  // Update the scroll effect
  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (!hasExpandedRef.current && calculatorMode === "DSCR" && !isMobile) {
  //       const dscrSection = document.getElementById("pdf-5");
  //       if (dscrSection) {
  //         const rect = dscrSection.getBoundingClientRect();
  //         const isLiftedOffBottom = rect.bottom < window.innerHeight;

  //         if (isLiftedOffBottom) {
  //           setShowQuestionDescription((prev) => ({
  //             ...prev,
  //             DSCR: true,
  //           }));
  //           hasExpandedRef.current = true; // Mark as expanded
  //         }
  //       }
  //     }
  //   };

  //   window.addEventListener("scroll", handleScroll, { passive: true });

  //   return () => {
  //     window.removeEventListener("scroll", handleScroll);
  //   };
  // }, [calculatorMode]); // Add calculatorMode to dependencies

  // Set initial state based on calculatorMode
  useEffect(() => {
    if (calculatorMode === "DSCR loan") {
      setShowQuestionDescription((prev) => ({
        ...prev,
        // "Cash flow and cash to close": true,
        // "Monthly cashflow": true,
        // "Cash to close": true,
      }));
    }
  }, [calculatorMode]);

  // Initialize original calculated values for loanTermsUI mode from externalCalc
  useEffect(() => {
    if (loanTermsUI && externalCalc && externalCalc.Answers) {
      // Only set if not already initialized to preserve true original values

      if (originalValuesRef.current.dscr === null) {
        // Extract calculated values from the loaded externalCalc.Answers
        const dscrAnswer = externalCalc.Answers.find(
          (answer) => answer.q_title === "DSCR"
        );

        const monthlyCashflowAnswer = externalCalc.Answers.find(
          (answer) => answer.q_title === "Monthly cashflow"
        );
        const cashToCloseAnswer = externalCalc.Answers.find(
          (answer) => answer.q_title === "Cash to close"
        );

        const liquidityToVerifyAnswer = externalCalc.Answers.find(
          (answer) => answer.q_title === "Liquidity to verify"
        );

        originalValuesRef.current = {
          ...originalValuesRef.current,
          dscr: dscrAnswer ? parseFloat(dscrAnswer.a_string) || 0 : null,
          monthlyCashflow: monthlyCashflowAnswer
            ? parseFloat(monthlyCashflowAnswer.a_string) || 0
            : null,
          cashToClose: cashToCloseAnswer
            ? parseFloat(cashToCloseAnswer.a_string) || 0
            : null,
          liquidityToVerify: liquidityToVerifyAnswer
            ? parseFloat(liquidityToVerifyAnswer.a_string) || 0
            : null,
        };
      }
    }
  }, [loanTermsUI, externalCalc]);

  // Add useEffect to manage exclusive showing of descriptions
  useEffect(() => {
    const descriptionsToManage = [
      "DSCR",
      "Monthly cashflow",
      "Cash to close",
      "Liquidity to verify",
    ];
    const activeDescription = descriptionsToManage.find(
      (key) => showQuestionDescription[key]
    );

    if (activeDescription) {
      const updatedShowQuestionDescription = { ...showQuestionDescription };

      descriptionsToManage.forEach((key) => {
        if (key !== activeDescription) {
          updatedShowQuestionDescription[key] = false;
        }
      });

      if (
        JSON.stringify(updatedShowQuestionDescription) !==
        JSON.stringify(showQuestionDescription)
      ) {
        setShowQuestionDescription(updatedShowQuestionDescription);
      }
    }
  }, [
    showQuestionDescription["DSCR"],
    showQuestionDescription["Monthly cashflow"],
    showQuestionDescription["Cash to close"],
    showQuestionDescription["Liquidity to verify"],
  ]);

  // Call getDSCRLTVTiers when in loanTermsUI mode and loan is provided
  useEffect(() => {
    if (loanTermsUI && loan) {
      (async () => {
        await getDSCRLTVTiers(loan.Answers, setLoanQuoteData, loan.id);
        const prepaymentOptions = await getDSCRPrepaymentOptions(user, loan.id);
        const buydownOptions = await getDSCRBuydownOptions(user, loan.id);

        // Set the prepayment options in state
        setPrepaymentOptions(prepaymentOptions || {});
        setBuydownOptions(buydownOptions || {});
      })();
    }
  }, [loanTermsUI, loan]);

  // Shared function to calculate and update interest rate
  const updateInterestRate = (updatedAnswers, answers) => {
    const treasuryRateAnswer = updatedAnswers.find(
      (a) => a.q_title === "5 year treasury"
    );
    const creditSpreadAnswer = updatedAnswers.find(
      (a) => a.q_title === "Credit spread"
    );
    const prepaymentSpreadAnswer = updatedAnswers.find(
      (a) => a.q_title === "Prepayment spread"
    );
    const buydownSpreadAnswer = updatedAnswers.find(
      (a) => a.q_title === "Buydown spread"
    );

    const treasuryRate = parseFloat(treasuryRateAnswer?.a_string || 0);
    const creditSpread = parseFloat(creditSpreadAnswer?.a_string || 0);
    const prepaymentSpread = parseFloat(prepaymentSpreadAnswer?.a_string || 0);
    const buydownSpread = parseFloat(buydownSpreadAnswer?.a_string || 0);

    if (
      !isNaN(treasuryRate) &&
      !isNaN(creditSpread) &&
      !isNaN(prepaymentSpread) &&
      !isNaN(buydownSpread)
    ) {
      const newInterestRate = (
        treasuryRate +
        creditSpread +
        prepaymentSpread +
        buydownSpread
      ).toFixed(2);
      const interestRateAnswer = updatedAnswers.find(
        (answer) => answer.q_title === "Interest rate"
      );

      if (interestRateAnswer) {
        interestRateAnswer.a_string = newInterestRate;

        // Track Interest rate difference for display
        if (!originalValuesRef.current["Interest rate"]) {
          originalValuesRef.current["Interest rate"] =
            answers.find((a) => a.q_title === "Interest rate")?.a_string || "";
        }

        const originalInterestRate =
          parseFloat(originalValuesRef.current["Interest rate"]) || 0;
        const interestRateDifference =
          parseFloat(newInterestRate) - originalInterestRate;
        if (Math.abs(interestRateDifference) > 0.01) {
          setValueChanges((prev) => ({
            ...prev,
            "Interest rate": interestRateDifference,
          }));
        } else {
          setValueChanges((prev) => {
            const newChanges = { ...prev };
            delete newChanges["Interest rate"];
            return newChanges;
          });
        }
      }
    }
  };

  const handleUserSelection = async (
    value,
    answer,
    answers,
    questions,
    setAnswers,
    isUserInteraction = false
  ) => {
    const questionsSlice = questions.slice();
    const updatedAnswers = answers.slice(0);

    let additionalData = {};
    if (loanTermsUI && loan) {
      additionalData = loan;
    }
    const isValidAnswer = answer.q_validation_func(
      value,
      answers,
      additionalData
    );
    const valueChange = valueChanges[answer.q_title];

    // Track when user makes changes in loanTermsUI mode
    // Only set this flag for actual user interactions, not initial load calculations
    if (
      loanTermsUI &&
      !userHasChangedValuesInLoanTermsUI &&
      isUserInteraction
    ) {
      setUserHasChangedValuesInLoanTermsUI(true);
      hasUserInteractedInLoanTermsUIRef.current = true;

      // Only initialize original calculated values if not already set from externalCalc.Answers
      if (originalValuesRef.current.dscr === null) {
        originalValuesRef.current = {
          ...originalValuesRef.current,
          dscr: dscr,
          monthlyCashflow: monthlyCashflow,
          cashToClose: cashToClose,
          liquidityToVerify: liquidityToVerify,
        };
      }

      // Reset version differences since user is now making dynamic changes
      setCalculatedValueDifferences({});
    }

    let updatedCurrentValues = {};
    if (isValidAnswer) {
      const targetAnswer = updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0];

      // Clear original values for calculated fields only when loan structure fundamentally changes
      const fieldsRequiringCalculatedReset = [
        "Loan amortization", // Changes payment calculation method (fully amortizing vs interest only)
        "Loan purpose", // Changes entire loan structure
      ];

      if (fieldsRequiringCalculatedReset.includes(answer.q_title)) {
        // Clear ALL calculated field originals for fundamental changes
        const calculatedFields = [
          "dscr",
          "monthlyCashflow",
          "cashToClose",
          "liquidityToVerify",
          "Monthly payment",
        ];

        calculatedFields.forEach((field) => {
          if (originalValuesRef.current[field] !== undefined) {
            delete originalValuesRef.current[field];
          }
        });
      }

      // Calculate value change for some types
      if (
        answer.a_type === "currency" ||
        answer.a_type === "number" ||
        answer.a_type === "percent" ||
        answer.a_type === "text"
      ) {
        if (!originalValuesRef.current[answer.q_title]) {
          // Get the actual current value from the answer object
          // however, if loan type has changed, answer was cleared... s0..
          // unless the loan type was changed... then its equal to the currentValue

          if (answer.a_string) {
            originalValuesRef.current[answer.q_title] = answer.a_string;
          } else {
            originalValuesRef.current[answer.q_title] = value;
          }
        }

        let newValue = value;
        let oldValue = originalValuesRef.current[answer.q_title];

        // Convert to numbers for numeric types
        if (
          answer.a_type === "currency" ||
          answer.a_type === "number" ||
          answer.a_type === "percent"
        ) {
          newValue = parseFloat(value) || 0;
          oldValue = parseFloat(oldValue) || 0;
        }

        const difference =
          answer.a_type === "text"
            ? newValue !== oldValue
            : newValue - oldValue;

        if (difference !== 0 && difference !== false) {
          setValueChanges((prev) => ({
            ...prev,
            [answer.q_title]: difference,
          }));
        } else {
          // If no difference, remove from valueChanges
          setValueChanges((prev) => {
            const newChanges = { ...prev };
            delete newChanges[answer.q_title];
            return newChanges;
          });
        }
      }

      // save users answer

      targetAnswer.a_string = value;
      // reset error just in case
      targetAnswer.a_error = false;

      // Special handling for buydown points changes to update buydown spread and interest rate
      if (answer.q_title === "Buydown points") {
        const selectedBuydownKey = value;

        // Look up the spread value from buydownOptions
        const buydownSpreadValue =
          buydownOptions[selectedBuydownKey]?.creditSpread || 0;

        // Update the Buydown spread answer
        const buydownSpreadAnswer = updatedAnswers.find(
          (a) => a.q_title === "Buydown spread"
        );

        if (buydownSpreadAnswer) {
          buydownSpreadAnswer.a_string = buydownSpreadValue.toString();

          // Track Buydown spread difference for display
          if (!originalValuesRef.current["Buydown spread"]) {
            originalValuesRef.current["Buydown spread"] =
              answers.find((a) => a.q_title === "Buydown spread")?.a_string ||
              "";
          }

          const originalBuydownSpread =
            parseFloat(originalValuesRef.current["Buydown spread"]) || 0;
          const buydownSpreadDifference =
            parseFloat(buydownSpreadValue) - originalBuydownSpread;
          if (Math.abs(buydownSpreadDifference) > 0.01) {
            setValueChanges((prev) => ({
              ...prev,
              "Buydown spread": buydownSpreadDifference,
            }));
          } else {
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges["Buydown spread"];
              return newChanges;
            });
          }
        }

        // Clear currentValues for buydown spread so UI shows the new dropdown value
        setCurrentValues((prev) => {
          const newCurrentValues = { ...prev };
          delete newCurrentValues["Buydown spread"];
          return newCurrentValues;
        });

        // Also clear from updatedCurrentValues to prevent restoration at the end
        delete updatedCurrentValues["Buydown spread"];

        // Update interest rate using shared function (this will trigger downstream calculations)

        updateInterestRate(updatedAnswers, answers);
      }

      // Special handling for LTV changes to update credit spread based on LTV tiers
      if (answer.q_title === "Loan to value (LTV)" && loanQuoteData?.LTVObj) {
        const selectedLTV = parseFloat(value);

        // Find the appropriate LTV tier (find the lowest tier that is >= selectedLTV)
        const ltvTiers = Object.keys(loanQuoteData.LTVObj)
          .map((ltv) => parseInt(ltv))
          .sort((a, b) => a - b); // Sort ascending

        const applicableTier = ltvTiers.find((tier) => tier >= selectedLTV);

        if (applicableTier) {
          const tierData = loanQuoteData.LTVObj[applicableTier];
          const creditSpread = tierData.creditSpread;

          // Update the credit spread answer
          const creditSpreadAnswer = updatedAnswers.find(
            (answer) => answer.q_title === "Credit spread"
          );

          if (creditSpreadAnswer) {
            creditSpreadAnswer.a_string = creditSpread;

            // Track Credit spread difference for display
            // Initialize original value if not already captured
            if (!originalValuesRef.current["Credit spread"]) {
              originalValuesRef.current["Credit spread"] =
                answers.find((a) => a.q_title === "Credit spread")?.a_string ||
                "";
            }

            const originalCreditSpread =
              parseFloat(originalValuesRef.current["Credit spread"]) || 0;
            const creditSpreadDifference =
              parseFloat(creditSpread) - originalCreditSpread;
            if (Math.abs(creditSpreadDifference) > 0.01) {
              setValueChanges((prev) => ({
                ...prev,
                "Credit spread": creditSpreadDifference,
              }));
            } else {
              // Remove diff if no significant change
              setValueChanges((prev) => {
                const newChanges = { ...prev };
                delete newChanges["Credit spread"];
                return newChanges;
              });
            }
          }

          // Update the interest rate using shared function
          updateInterestRate(updatedAnswers, answers);
        }
      }

      // Special handling for 5 year treasury changes to update interest rate and dependent calculations
      if (answer.q_title === "5 year treasury") {
        const treasuryRate = parseFloat(value);
        setCurrent5yrTreasury(treasuryRate);

        // Update interest rate using shared function
        updateInterestRate(updatedAnswers, answers);
      }

      // Special handling for prepayment penalty changes to update prepayment spread and interest rate
      if (answer.q_title === "Prepayment penalty") {
        const selectedPrepaymentKey = value;

        // Look up the spread value from prepaymentOptions
        const prepaymentSpreadValue =
          prepaymentOptions[selectedPrepaymentKey]?.creditSpread || 0;

        // Update the Prepayment spread answer
        const prepaymentSpreadAnswer = updatedAnswers.find(
          (a) => a.q_title === "Prepayment spread"
        );

        if (prepaymentSpreadAnswer) {
          prepaymentSpreadAnswer.a_string = prepaymentSpreadValue.toString();

          // Track Prepayment spread difference for display
          if (!originalValuesRef.current["Prepayment spread"]) {
            originalValuesRef.current["Prepayment spread"] =
              answers.find((a) => a.q_title === "Prepayment spread")
                ?.a_string || "";
          }

          const originalPrepaymentSpread =
            parseFloat(originalValuesRef.current["Prepayment spread"]) || 0;
          const prepaymentSpreadDifference =
            parseFloat(prepaymentSpreadValue) - originalPrepaymentSpread;
          if (Math.abs(prepaymentSpreadDifference) > 0.01) {
            setValueChanges((prev) => ({
              ...prev,
              "Prepayment spread": prepaymentSpreadDifference,
            }));
          } else {
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges["Prepayment spread"];
              return newChanges;
            });
          }
        }

        // Update interest rate using shared function (this will trigger downstream calculations)
        updateInterestRate(updatedAnswers, answers);
      }

      // Special handling for manual prepayment spread changes by OM team
      if (answer.q_title === "Prepayment spread" && isOMTeam) {
        const prepaymentSpreadValue = parseFloat(value) || 0;

        // Track Prepayment spread difference for display
        if (!originalValuesRef.current["Prepayment spread"]) {
          originalValuesRef.current["Prepayment spread"] =
            answers.find((a) => a.q_title === "Prepayment spread")?.a_string ||
            "";
        }

        const originalPrepaymentSpread =
          parseFloat(originalValuesRef.current["Prepayment spread"]) || 0;
        const prepaymentSpreadDifference =
          prepaymentSpreadValue - originalPrepaymentSpread;

        if (Math.abs(prepaymentSpreadDifference) > 0.01) {
          setValueChanges((prev) => ({
            ...prev,
            "Prepayment spread": prepaymentSpreadDifference,
          }));
        } else {
          setValueChanges((prev) => {
            const newChanges = { ...prev };
            delete newChanges["Prepayment spread"];
            return newChanges;
          });
        }

        // Update interest rate using shared function (this will trigger downstream calculations)
        updateInterestRate(updatedAnswers, answers);
      }

      // Special handling for manual buydown spread changes by OM team
      if (answer.q_title === "Buydown spread" && isOMTeam) {
        const buydownSpreadValue = parseFloat(value) || 0;

        // Track Buydown spread difference for display
        if (!originalValuesRef.current["Buydown spread"]) {
          originalValuesRef.current["Buydown spread"] =
            answers.find((a) => a.q_title === "Buydown spread")?.a_string || "";
        }

        const originalBuydownSpread =
          parseFloat(originalValuesRef.current["Buydown spread"]) || 0;
        const buydownSpreadDifference =
          buydownSpreadValue - originalBuydownSpread;

        if (Math.abs(buydownSpreadDifference) > 0.01) {
          setValueChanges((prev) => ({
            ...prev,
            "Buydown spread": buydownSpreadDifference,
          }));
        } else {
          setValueChanges((prev) => {
            const newChanges = { ...prev };
            delete newChanges["Buydown spread"];
            return newChanges;
          });
        }

        // Update interest rate using shared function (this will trigger downstream calculations)
        updateInterestRate(updatedAnswers, answers);
      }

      // determine if need to insert follow up questions
      const next_q_id = answer.q_next_step_func(answer, answers, questions);

      // NEXT QUESTION is always next in position
      // NOT like before when it was based on length of q_id
      // this was limiting in terms of folding questions of the tree back
      // and allowing the tree to merge back to left most path
      const posOfNextQuestion =
        updatedAnswers
          .map((updatedAnswer) => {
            return updatedAnswer.q_id;
          })
          .indexOf(answer.q_id) + 1;

      const nextPotentialQuestion = updatedAnswers[posOfNextQuestion];

      if (
        next_q_id &&
        nextPotentialQuestion &&
        nextPotentialQuestion?.q_id !== next_q_id
      ) {
        // OFF LEFT MOST PATH
        const newNextQuestion = questionsSlice.filter((question) => {
          return question.q_id === next_q_id;
        })[0];

        // LEAF, remove all subsequent questions from answers
        if (newNextQuestion.node_type === "leaf") {
          // if a LEAF never delete anything (like using slice)
          // instead add hidden property
          for (let i = posOfNextQuestion; i < updatedAnswers.length; i++) {
            updatedAnswers[i].node_visibility = "hidden";
          }
        } else {
          // NODE, make sure subsequent questions are prents
          // need to make left most path starting from next questions's id.
          let leftMostPathFromCurrentNode = [];
          let deeperNextQuestion = newNextQuestion;
          let deeperNextQuestionChildrenLength =
            deeperNextQuestion.q_child_nodes.length;
          const map_q_id = questionsSlice.map((question) => {
            return question.q_id;
          });

          while (deeperNextQuestionChildrenLength !== 0) {
            deeperNextQuestion =
              questions[
                map_q_id.indexOf(
                  deeperNextQuestion.q_next_step_func(
                    deeperNextQuestion,
                    [],
                    []
                  )
                )
              ];

            if (deeperNextQuestion) {
              deeperNextQuestionChildrenLength =
                deeperNextQuestion.q_child_nodes.length;

              leftMostPathFromCurrentNode.push(deeperNextQuestion);
            } else {
              deeperNextQuestionChildrenLength = 0;
            }
          }

          let counter = 0;
          for (
            let i = posOfNextQuestion + 1;
            i < leftMostPathFromCurrentNode.length + posOfNextQuestion + 1;
            i++
          ) {
            if (
              updatedAnswers[i]?.q_id === leftMostPathFromCurrentNode[counter]
            ) {
              //show the node
              // updatedAnswers[i].node_type = "node";
            } else {
              //or overwrite if different but preserve a_string
              const existingAString = updatedAnswers[i]?.a_string;
              updatedAnswers[i] = leftMostPathFromCurrentNode[counter];
              if (existingAString) {
                updatedAnswers[i].a_string = existingAString;
              }
              // updatedAnswers[i].node_type = "node";
            }
            counter = counter + 1;
          }

          // make sure all nodes are re-updated
          // to have node_type be node so nothing is hidden
          // if people jump around
          for (let i = 0; i < updatedAnswers.length; i++) {
            if (updatedAnswers[i].node_type === "node") {
              updatedAnswers[i].node_visibility = "visible";
            }
          }
        }

        const existingAString = updatedAnswers[posOfNextQuestion]?.a_string;

        updatedAnswers[posOfNextQuestion] = questionsSlice.filter(
          (question) => {
            return question.q_id === next_q_id;
          }
        )[0];

        if (existingAString) {
          updatedAnswers[posOfNextQuestion].a_string = existingAString;
        }
        updatedAnswers[posOfNextQuestion].node_visibility = "visible";
      }

      // UNIQUE TO DSCR CALCULATOR
      // set refinance type to be always cash out
      // updatedAnswers

      const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
        return answer?.q_title === "Refinance type";
      });

      toUpdateAnswer.a_string = "Cash out refinance";

      // ===== INTEGRATED CALCULATION LOGIC FROM useEffect([answers]) =====

      // Handle Loan purpose special case
      if (answer.q_title === "Loan purpose") {
        // Reset all subsequent questions except preserved keys

        updatedAnswers.forEach((answer) => {
          if (!preserveKeys.includes(answer.q_title)) {
            answer.a_string = "";
          }
        });
        // Also clear all currentvalues that aren't preserveKeys
        setCurrentValues((prev) => {
          if (!prev) return prev;
          const newCurrentValues = { ...prev };
          Object.keys(newCurrentValues).forEach((key) => {
            if (!preserveKeys.includes(key)) {
              delete newCurrentValues[key];
            }
          });
          return newCurrentValues;
        });

        // Reset calculated output fields
        setDSCR(0);
        setMonthlyCashflow(0);
        setCashToClose(0);
        setCashToCloseIsPositive(false);
        setBuydownAmount(0);

        // Clear out all non-preserved keys from originalValuesRef, and set preserved keys if not already set

        Object.keys(originalValuesRef.current).forEach((key) => {
          if (!preserveKeys.includes(key)) {
            delete originalValuesRef.current[key];
          }
        });

        // Reset valueChanges (diffs) for non-preserved keys
        setValueChanges((prev) => {
          const newValueChanges = { ...prev };
          Object.keys(newValueChanges).forEach((key) => {
            if (!preserveKeys.includes(key)) {
              delete newValueChanges[key];
            }
          });
          return newValueChanges;
        });

        // Reset loanTermsUI tracking refs since loan context has changed
        hasUserInteractedInLoanTermsUIRef.current = false;
        initialLoadValuesRef.current = {};
      }

      // ===== EXTRACT VALUES FROM ANSWERS =====
      const creditScore = updatedAnswers.find(
        (a) => a.q_title === "Credit score"
      )?.a_string;
      setCreditScore(creditScore);

      const loanPurpose = updatedAnswers.find(
        (a) => a.q_title === "Loan purpose"
      )?.a_string;
      setLoanPurpose(loanPurpose);

      // Calculate property value based on loan purpose
      let propertyValue;
      if (loanPurpose === "Purchase") {
        propertyValue = updatedAnswers.find(
          (a) => a.q_title === "Purchase price"
        )?.a_string;
      } else if (loanPurpose === "Refinance") {
        propertyValue = updatedAnswers.find(
          (a) => a.q_title === "Estimated AS-IS value"
        )?.a_string;
      }
      setPropertyValue(propertyValue);

      // ===== HANDLE BIDIRECTIONAL PURCHASE PRICE/LOAN AMOUNT/LTV UPDATES =====
      let loanAmount = updatedAnswers.find(
        (a) => a.q_title === "Loan amount"
      )?.a_string;
      let LTV = 0;

      if (answer.q_title === "Purchase price") {
        // When Purchase Price changes, update Loan Amount to maintain current LTV
        const currentLTV =
          updatedAnswers.find((a) => a.q_title === "Loan to value (LTV)")
            ?.a_string || 0;

        if (currentLTV && parseFloat(currentLTV) > 0) {
          loanAmount = (
            parseFloat(value) *
            (parseFloat(currentLTV) / 100)
          ).toFixed(2);
          const loanAmountAnswer = updatedAnswers.find(
            (a) => a.q_title === "Loan amount"
          );
          if (loanAmountAnswer) {
            loanAmountAnswer.a_string = loanAmount;
          }

          // Track Loan Amount difference for display
          // Initialize original value if not already captured
          if (!originalValuesRef.current["Loan amount"]) {
            originalValuesRef.current["Loan amount"] =
              answers.find((a) => a.q_title === "Loan amount")?.a_string || "";
          }

          const originalLoanAmount =
            parseFloat(originalValuesRef.current["Loan amount"]) || 0;
          const loanAmountDifference =
            parseFloat(loanAmount) - originalLoanAmount;
          if (Math.abs(loanAmountDifference) > 0.01) {
            setValueChanges((prev) => ({
              ...prev,
              "Loan amount": loanAmountDifference,
            }));
          } else {
            // Remove diff if no significant change
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges["Loan amount"];
              return newChanges;
            });
          }
        }
        LTV = parseFloat(currentLTV);
      } else if (answer.q_title === "Estimated AS-IS value") {
        // When Estimated AS-IS value (for refinance) changes, update Loan Amount to maintain current LTV
        const currentLTV =
          updatedAnswers.find((a) => a.q_title === "Loan to value (LTV)")
            ?.a_string || 0;

        if (currentLTV && parseFloat(currentLTV) > 0) {
          loanAmount = (
            parseFloat(value) *
            (parseFloat(currentLTV) / 100)
          ).toFixed(2);
          const loanAmountAnswer = updatedAnswers.find(
            (a) => a.q_title === "Loan amount"
          );
          if (loanAmountAnswer) {
            loanAmountAnswer.a_string = loanAmount;
          }

          // Track Loan Amount difference for display
          // Initialize original value if not already captured
          if (!originalValuesRef.current["Loan amount"]) {
            originalValuesRef.current["Loan amount"] =
              answers.find((a) => a.q_title === "Loan amount")?.a_string || "";
          }

          const originalLoanAmount =
            parseFloat(originalValuesRef.current["Loan amount"]) || 0;
          const loanAmountDifference =
            parseFloat(loanAmount) - originalLoanAmount;
          if (Math.abs(loanAmountDifference) > 0.01) {
            setValueChanges((prev) => ({
              ...prev,
              "Loan amount": loanAmountDifference,
            }));
          } else {
            // Remove diff if no significant change
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges["Loan amount"];
              return newChanges;
            });
          }
        }
        LTV = parseFloat(currentLTV);
      } else if (answer.q_title === "Loan amount") {
        LTV = ((parseFloat(value) / propertyValue) * 100).toFixed(2);
        const ltvAnswer = updatedAnswers.find(
          (a) => a.q_title === "Loan to value (LTV)"
        );
        if (ltvAnswer) ltvAnswer.a_string = LTV;

        // Update just the LTV answer in the current state (for display)
        setAnswers((prevAnswers) => {
          const newAnswers = [...prevAnswers];
          const currentLtvAnswer = newAnswers.find(
            (a) => a.q_title === "Loan to value (LTV)"
          );
          if (currentLtvAnswer) {
            currentLtvAnswer.a_string = LTV;
          }
          return newAnswers;
        });

        // Track LTV difference for display
        // Initialize original value if not already captured
        if (!originalValuesRef.current["Loan to value (LTV)"]) {
          originalValuesRef.current["Loan to value (LTV)"] =
            answers.find((a) => a.q_title === "Loan to value (LTV)")
              ?.a_string || "";
        }

        const originalLTV =
          parseFloat(originalValuesRef.current["Loan to value (LTV)"]) || 0;
        const ltvDifference = parseFloat(LTV) - originalLTV;
        if (Math.abs(ltvDifference) > 0.01) {
          setValueChanges((prev) => ({
            ...prev,
            "Loan to value (LTV)": ltvDifference,
          }));
        } else {
          // Remove diff if no significant change
          setValueChanges((prev) => {
            const newChanges = { ...prev };
            delete newChanges["Loan to value (LTV)"];
            return newChanges;
          });
        }

        // Trigger LTV-dependent calculations (credit spread, etc.)
        if (loanQuoteData?.LTVObj) {
          const selectedLTV = parseFloat(LTV);

          // Find the appropriate LTV tier (find the lowest tier that is >= selectedLTV)
          const ltvTiers = Object.keys(loanQuoteData.LTVObj)
            .map((ltv) => parseInt(ltv))
            .sort((a, b) => a - b); // Sort ascending

          const applicableTier = ltvTiers.find((tier) => tier >= selectedLTV);

          if (applicableTier) {
            const tierData = loanQuoteData.LTVObj[applicableTier];
            const creditSpread = tierData.creditSpread;

            // Update the credit spread answer
            const creditSpreadAnswer = updatedAnswers.find(
              (answer) => answer.q_title === "Credit spread"
            );

            if (creditSpreadAnswer) {
              creditSpreadAnswer.a_string = creditSpread;

              // Update just the Credit spread answer in the current state (for display)
              setAnswers((prevAnswers) => {
                const newAnswers = [...prevAnswers];
                const currentCreditSpreadAnswer = newAnswers.find(
                  (a) => a.q_title === "Credit spread"
                );
                if (currentCreditSpreadAnswer) {
                  currentCreditSpreadAnswer.a_string = creditSpread;
                }
                return newAnswers;
              });

              // Also update currentValues so UI displays the cascaded value immediately
              setCurrentValues((prev) => ({
                ...prev,
                "Credit spread": creditSpread,
              }));

              // Track Credit spread difference for display
              // Initialize original value if not already captured
              if (!originalValuesRef.current["Credit spread"]) {
                originalValuesRef.current["Credit spread"] =
                  answers.find((a) => a.q_title === "Credit spread")
                    ?.a_string || "";
              }

              const originalCreditSpread =
                parseFloat(originalValuesRef.current["Credit spread"]) || 0;
              const creditSpreadDifference =
                parseFloat(creditSpread) - originalCreditSpread;
              if (Math.abs(creditSpreadDifference) > 0.01) {
                setValueChanges((prev) => ({
                  ...prev,
                  "Credit spread": creditSpreadDifference,
                }));
              } else {
                // Remove diff if no significant change
                setValueChanges((prev) => {
                  const newChanges = { ...prev };
                  delete newChanges["Credit spread"];
                  return newChanges;
                });
              }

              // Update Interest Rate since credit spread changed
              updateInterestRate(updatedAnswers);

              // Update the interest rate in current state for display
              const interestRateAnswer = updatedAnswers.find(
                (answer) => answer.q_title === "Interest rate"
              );
              if (interestRateAnswer) {
                setAnswers((prevAnswers) => {
                  const newAnswers = [...prevAnswers];
                  const currentInterestRateAnswer = newAnswers.find(
                    (a) => a.q_title === "Interest rate"
                  );
                  if (currentInterestRateAnswer) {
                    currentInterestRateAnswer.a_string =
                      interestRateAnswer.a_string;
                  }
                  return newAnswers;
                });
              }
            }
          }
        }
      } else if (answer.q_title === "Loan to value (LTV)") {
        // When LTV changes, update Loan Amount to maintain property value relationship
        if (propertyValue && parseFloat(value) > 0) {
          loanAmount = (
            parseFloat(propertyValue) *
            (parseFloat(value) / 100)
          ).toFixed(2);

          const loanAmountAnswer = updatedAnswers.find(
            (a) => a.q_title === "Loan amount"
          );
          if (loanAmountAnswer) {
            loanAmountAnswer.a_string = loanAmount;
          }

          // Track Loan Amount difference for display
          // Initialize original value if not already captured
          if (!originalValuesRef.current["Loan amount"]) {
            originalValuesRef.current["Loan amount"] =
              answers.find((a) => a.q_title === "Loan amount")?.a_string || "";
          }

          const originalLoanAmount =
            parseFloat(originalValuesRef.current["Loan amount"]) || 0;
          const loanAmountDifference =
            parseFloat(loanAmount) - originalLoanAmount;
          if (Math.abs(loanAmountDifference) > 0.01) {
            setValueChanges((prev) => ({
              ...prev,
              "Loan amount": loanAmountDifference,
            }));
          } else {
            // Remove diff if no significant change
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges["Loan amount"];
              return newChanges;
            });
          }
        }
        LTV = parseFloat(value);
      } else {
        // Get current values
        if (loanAmount) {
          LTV = ((loanAmount / propertyValue) * 100).toFixed(2);
        } else {
          LTV = updatedAnswers.find(
            (a) => a.q_title === "Loan to value (LTV)"
          )?.a_string;
        }
        if (!loanAmount && LTV) {
          loanAmount = (propertyValue * (LTV / 100)).toFixed(2);
          // Update the loan amount in updatedAnswers so it persists
          const loanAmountAnswer = updatedAnswers.find(
            (a) => a.q_title === "Loan amount"
          );
          if (loanAmountAnswer) {
            loanAmountAnswer.a_string = loanAmount;
          }
        }
      }

      setLoanAmount(loanAmount);
      // Re-pull origination fee based on new loan amount via backend helper
      try {
        let resp;

        resp = await getOriginationFee(loanAmount, numberOfProperties);

        let newOriginationFee = resp?.fee ?? resp?.data?.fee ?? resp;
        const newOriginationFeeExplanation =
          resp?.explanation ?? resp?.data?.explanation ?? "";

        if (typeof newOriginationFee === "string") {
          const parsed = parseFloat(newOriginationFee);
          if (!Number.isNaN(parsed)) newOriginationFee = parsed;
        }
        if (Number.isFinite(newOriginationFee)) {
          const originationFeeAnswer = updatedAnswers.find(
            (a) => a.q_title === "Origination fee"
          );
          if (originationFeeAnswer)
            originationFeeAnswer.a_string = newOriginationFee;
          setOriginationFee(newOriginationFee);
          setOriginationFeeExplanation(newOriginationFeeExplanation || "");
          setCurrentValues((prev) => ({
            ...prev,
            "Origination fee": newOriginationFee,
          }));
        }
      } catch (e) {
        console.error("Failed to refresh origination fee:", e);
      }

      setLTV(LTV);

      // ===== EXTRACT OTHER VALUES =====
      let localInterestRate;
      const interestRate = updatedAnswers.find(
        (a) => a.q_title === "Interest rate"
      )?.a_string;
      const buydownPoints =
        updatedAnswers.find((a) => a.q_title === "Buydown points")?.a_string ||
        0;

      if (interestRate) {
        localInterestRate = interestRate;
      } else {
        localInterestRate =
          updatedAnswers.find((a) => a.q_title === "Interest rate")?.a_string ||
          0;
      }

      // Apply buydown points to interest rate (typically 0.25% reduction per point)
      const adjustedInterestRate = Math.max(
        0,
        parseFloat(localInterestRate) - parseFloat(buydownPoints) * 0.25
      );
      setLocalInterestRate(adjustedInterestRate);

      // Calculate buydown amount (each point costs 1% of loan amount)
      const buydownAmount =
        (parseFloat(buydownPoints) * parseFloat(loanAmount)) / 100;
      setBuydownAmount(buydownAmount);
      // <><>!
      const buydownSpread =
        updatedAnswers.find((a) => a.q_title === "Buydown spread")?.a_string ||
        0;
      setBuydownSpread(buydownSpread);
      const buydownAmountAnswer = updatedAnswers.find(
        (a) => a.q_title === "Buydown amount"
      );
      if (buydownAmountAnswer) {
        buydownAmountAnswer.a_string = buydownAmount.toString();

        // Track Buydown amount difference for display
        if (!originalValuesRef.current["Buydown amount"]) {
          originalValuesRef.current["Buydown amount"] =
            answers.find((a) => a.q_title === "Buydown amount")?.a_string || "";
        }

        const originalBuydownAmount =
          parseFloat(originalValuesRef.current["Buydown amount"]) || 0;
        const buydownAmountDifference = buydownAmount - originalBuydownAmount;
        if (Math.abs(buydownAmountDifference) > 0.01) {
          setValueChanges((prev) => ({
            ...prev,
            "Buydown amount": buydownAmountDifference,
          }));
        } else {
          setValueChanges((prev) => {
            const newChanges = { ...prev };
            delete newChanges["Buydown amount"];
            return newChanges;
          });
        }
      }

      const monthlyRent =
        updatedAnswers.find((a) => a.q_title === "Monthly rent")?.a_string || 0;
      const annualRent = monthlyRent * 12;
      setMonthlyRent(monthlyRent);
      setAnnualRent(annualRent);

      const annualTaxes =
        updatedAnswers.find((a) => a.q_title === "Annual taxes")?.a_string || 0;
      setAnnualTaxes(annualTaxes);

      const annualInsurance =
        updatedAnswers.find((a) => a.q_title === "Annual insurance")
          ?.a_string || 0;
      setAnnualInsurance(annualInsurance);

      const annualHOA =
        updatedAnswers.find((a) => a.q_title === "Annual HOA")?.a_string || 0;
      setAnnualHOA(annualHOA);

      const annualPropertyManagementExpense =
        updatedAnswers.find(
          (a) => a.q_title === "Annual property management expense"
        )?.a_string || 0;
      setAnnualPropertyManagementExpense(annualPropertyManagementExpense);

      const estimatedAsIsValue =
        updatedAnswers.find((a) => a.q_title === "Estimated AS-IS value")
          ?.a_string || 0;
      setEstimatedAsIsValue(estimatedAsIsValue);

      const existingDebt =
        updatedAnswers.find((a) => a.q_title === "Existing debt")?.a_string ||
        0;
      setExistingDebt(existingDebt);

      // ===== CALCULATE FEES =====
      let loanFees =
        updatedAnswers.find((a) => a.q_title === "Loan fees")?.a_string || 0;

      if (!loanFees) {
        loanFees = 1995;
        const loanFeesAnswer = updatedAnswers.find(
          (a) => a.q_title === "Loan fees"
        );
        if (loanFeesAnswer) loanFeesAnswer.a_string = loanFees;
      }
      setLoanFees(loanFees);

      let originationFee =
        updatedAnswers.find((a) => a.q_title === "Origination fee")?.a_string ||
        0;

      setOriginationFee(originationFee);

      let titleFees =
        updatedAnswers.find((a) => a.q_title === "Title fees")?.a_string || 0;
      if (!titleFees) {
        titleFees = parseInt(loanAmount) * 0.01;
        const titleFeesAnswer = updatedAnswers.find(
          (a) => a.q_title === "Title fees"
        );
        if (titleFeesAnswer) titleFeesAnswer.a_string = titleFees;
      }
      setTitleFees(titleFees);

      let escrow = updatedAnswers.find((a) => a.q_title === "Escrow")?.a_string;
      if (!escrow) {
        escrow =
          (parseInt(annualTaxes) * 3) / 12 +
          (parseInt(annualInsurance) * 3) / 12 +
          (parseInt(annualHOA) * 3) / 12;
        const escrowAnswer = updatedAnswers.find((a) => a.q_title === "Escrow");
        if (escrowAnswer) escrowAnswer.a_string = escrow;
      }
      setEscrow(escrow);

      let prepaidInterest =
        updatedAnswers.find((a) => a.q_title === "Prepaid interest")
          ?.a_string || 0;
      if (!prepaidInterest) {
        const today = dayjs();
        const endOfMonth = today.endOf("month");
        const daysUntilEndOfMonth = endOfMonth.diff(today, "day");
        prepaidInterest =
          ((loanAmount * (parseFloat(adjustedInterestRate) / 100)) / 365) *
          daysUntilEndOfMonth;
        const prepaidInterestAnswer = updatedAnswers.find(
          (a) => a.q_title === "Prepaid interest"
        );
        if (prepaidInterestAnswer)
          prepaidInterestAnswer.a_string = prepaidInterest;
      }
      setPrepaidInterest(prepaidInterest);

      let prepaidInsurance =
        updatedAnswers.find((a) => a.q_title === "Prepaid insurance")
          ?.a_string || 0;
      if (!prepaidInsurance) {
        prepaidInsurance = annualInsurance;
        const prepaidInsuranceAnswer = updatedAnswers.find(
          (a) => a.q_title === "Prepaid insurance"
        );
        if (prepaidInsuranceAnswer)
          prepaidInsuranceAnswer.a_string = prepaidInsurance;
      }
      setPrepaidInsurance(prepaidInsurance);

      let paymentReserveValue = updatedAnswers.find(
        (a) => a.q_title === "Payment reserve"
      )?.a_string;

      setPaymentReserve(paymentReserveValue || 0);

      // ===== PERFORM CALCULATIONS =====
      const numPmt = 12;
      const numYrs = 30;

      // Get loan amortization for payment calculation
      const loanAmortization = updatedAnswers.find(
        (a) => a.q_title === "Loan amortization"
      )?.a_string;

      const { monthlyPmt, dscr } = calculateMonthlyPmtAndDSCR({
        loanAmount,
        localInterestRate: adjustedInterestRate,
        numPmt,
        numYrs,
        annualRent,
        annualTaxes,
        annualInsurance,
        annualHOA,
        loanAmortization,
      });

      setMonthlyPmt(monthlyPmt);
      setDSCR(dscr);

      // Calculate cash to close
      let variableExpenseCashToClose = 0;
      if (loanPurpose === "Purchase") {
        variableExpenseCashToClose = propertyValue;
      } else {
        variableExpenseCashToClose = existingDebt;
      }
      setVariableExpenseCashToClose(variableExpenseCashToClose);

      const cashToClose =
        parseInt(variableExpenseCashToClose) -
        parseInt(loanAmount) +
        parseInt(originationFee || 0) +
        parseInt(loanFees || 0) +
        parseInt(titleFees || 0) +
        parseInt(escrow || 0) +
        parseInt(prepaidInterest || 0) +
        parseInt(prepaidInsurance || 0) +
        parseInt(paymentReserve) +
        parseInt(buydownAmount);

      setCashToClose(cashToClose);
      setCashToCloseIsPositive(cashToClose > 0);

      // Calculate liquidity to verify with PITIA reserve
      // Get citizenship status and determine months of PITIA for liqReq calculations
      const answersToUse =
        loanTermsUI && loan?.Answers ? loan.Answers : answers;
      const citizenshipStatus = answersToUse?.filter((answer) => {
        return answer.q_title === "Citizenship status";
      })[0]?.a_string;

      const calculatedPitiaReserveMonths =
        citizenshipStatus === "US citizen" ? 9 : citizenshipStatus ? 12 : 9; // Default to 9 for normal mode
      setPitiaReserveMonths(calculatedPitiaReserveMonths);
      const calculatedMonthlyPitia =
        parseInt(monthlyPmt) +
        parseInt(annualTaxes) / 12 +
        parseInt(annualInsurance) / 12 +
        parseInt(annualHOA) / 12;

      setMonthlyPitia(calculatedMonthlyPitia);

      // Always calculate liquidity to verify based on current inputs (like Cash to close)
      let liquidityToVerify;

      if (loanPurpose === "Refinance") {
        // For refinance: (closing costs + PITIA reserve) - (0.5 * net proceeds)
        const closingCosts =
          parseInt(originationFee || 0) +
          parseInt(loanFees || 0) +
          parseInt(titleFees || 0) +
          parseInt(escrow || 0) +
          parseInt(prepaidInterest || 0) +
          parseInt(prepaidInsurance || 0) +
          parseInt(buydownAmount || 0);

        const pitiaReserve =
          calculatedMonthlyPitia * calculatedPitiaReserveMonths;

        const netProceeds = Math.abs(parseInt(cashToClose || 0));

        liquidityToVerify = closingCosts + pitiaReserve - 0.5 * netProceeds;
      } else {
        // For purchase: original calculation
        liquidityToVerify =
          parseInt(variableExpenseCashToClose || 0) -
          parseInt(loanAmount || 0) +
          parseInt(originationFee || 0) +
          parseInt(loanFees || 0) +
          parseInt(titleFees || 0) +
          parseInt(escrow || 0) +
          parseInt(prepaidInterest || 0) +
          parseInt(prepaidInsurance || 0) +
          parseInt(buydownAmount || 0) +
          calculatedMonthlyPitia * calculatedPitiaReserveMonths; // PITIA reserve
      }

      setLiquidityToVerify(liquidityToVerify);

      const monthlyCashflow =
        parseInt(monthlyRent) -
        (parseInt(annualTaxes) / 12 +
          parseInt(annualInsurance) / 12 +
          parseInt(monthlyPmt) +
          parseInt(annualHOA) / 12);

      if (monthlyCashflow) {
        setMonthlyCashflow(monthlyCashflow);
      }

      // ===== DIFF TRACKING FOR CALCULATED FIELDS IS HANDLED BY THE CONSOLIDATED TRACKING BELOW =====

      // Track changes in calculated values
      const currentCalculatedValues = {
        dscr: dscr,
        monthlyCashflow: monthlyCashflow,
        cashToClose: cashToClose,
        liquidityToVerify: liquidityToVerify,
        "Monthly payment": calculatedMonthlyPitia,
      };

      // INITIAL LOAD LOGIC - Set original baselines (only run once per session)
      if (
        loanTermsUI &&
        !hasUserInteractedInLoanTermsUIRef.current &&
        previousCalc
      ) {
        // CASE 3: loanTermsUI = true && v2+ - Show initial diffs vs previous version

        // Store initial load values for later use when user starts typing
        Object.keys(currentCalculatedValues).forEach((key) => {
          if (!(key in initialLoadValuesRef.current)) {
            initialLoadValuesRef.current[key] = currentCalculatedValues[key];
          }
        });
        const newCalculatedChanges = {};

        Object.keys(currentCalculatedValues).forEach((key) => {
          const currentValue = currentCalculatedValues[key];
          const originalValue = originalValuesRef.current[key];

          if (
            originalValue !== null &&
            originalValue !== undefined &&
            currentValue !== originalValue
          ) {
            const difference = currentValue - originalValue;
            if (Math.abs(difference) > 0.01) {
              // Only show significant changes
              newCalculatedChanges[key] = difference;
            }
          }
        });

        setValueChanges((prev) => ({
          ...prev,
          ...newCalculatedChanges,
        }));
      } else if (!loanTermsUI) {
        // stopped here
        // CASE 1: loanTermsUI = false - Set initial baselines (no initial diffs)

        Object.keys(currentCalculatedValues).forEach((key) => {
          if (
            (originalValuesRef.current[key] === undefined ||
              originalValuesRef.current[key] === null) &&
            !isNaN(currentCalculatedValues[key]) // last condition, is to ensure after loan type change, when calculated values are NaN, won't be set, thus breaking the diff functionality
          ) {
            originalValuesRef.current[key] = currentCalculatedValues[key];
          }
        });
      } else if (loanTermsUI && !previousCalc) {
        // CASE 2: loanTermsUI = true && v1 - Set initial baselines (no initial diffs)
        Object.keys(currentCalculatedValues).forEach((key) => {
          if (
            originalValuesRef.current[key] === undefined ||
            originalValuesRef.current[key] === null
          ) {
            originalValuesRef.current[key] = currentCalculatedValues[key];
          }
        });
        setCalculatedValueDifferences({});
      } else if (
        loanTermsUI &&
        !hasUserInteractedInLoanTermsUIRef.current &&
        userHasChangedValuesInLoanTermsUI
      ) {
        // CASE 4: loanTermsUI = true && first user interaction - Switch from version diffs to user diffs

        // Reset baselines to initial load values (stored when page first loaded)
        Object.keys(currentCalculatedValues).forEach((key) => {
          const initialLoadValue = initialLoadValuesRef.current[key];
          if (initialLoadValue !== undefined && initialLoadValue !== null) {
            originalValuesRef.current[key] = initialLoadValue;
          }
        });

        // Mark that user has started interacting
        hasUserInteractedInLoanTermsUIRef.current = true;

        // Clear existing version diffs since we're switching to user diffs
        setValueChanges({});
      }

      // USER INTERACTION LOGIC - Always show diffs when user types (regardless of mode)
      // This runs every time handleUserSelection is called (which means user made a change)
      const newCalculatedChanges = {};

      Object.keys(currentCalculatedValues).forEach((key) => {
        const currentValue = currentCalculatedValues[key];
        const originalValue = originalValuesRef.current[key];

        if (
          originalValue !== null &&
          originalValue !== undefined &&
          currentValue !== originalValue
        ) {
          const difference = currentValue - originalValue;
          if (Math.abs(difference) > 0.01) {
            newCalculatedChanges[key] = difference;
          }
        }
      });

      setValueChanges((prev) => ({
        ...prev,
        ...newCalculatedChanges,
      }));

      // update current values to trigger pre-filled calculations
      // IMPORTANT: Use functional update to avoid stale closure state

      updatedCurrentValues = Object.assign({}, currentValues);

      // Update calculated fields
      updatedCurrentValues["Loan fees"] = loanFees;
      updatedCurrentValues["Escrow"] = escrow;
      updatedCurrentValues["Title fees"] = titleFees;
      updatedCurrentValues["Prepaid interest"] = prepaidInterest;
      updatedCurrentValues["Prepaid insurance"] = prepaidInsurance;
      updatedCurrentValues["Origination fee"] = originationFee;
      updatedCurrentValues["Payment reserve"] = paymentReserve;
      updatedCurrentValues["DSCR"] = dscr;
      updatedCurrentValues["Monthly payment"] = calculatedMonthlyPitia;
      updatedCurrentValues["Monthly cashflow"] = monthlyCashflow;
      updatedCurrentValues["Cash to close"] = cashToClose;
      updatedCurrentValues["Liquidity to verify"] = liquidityToVerify;
      updatedCurrentValues["Buydown amount"] = buydownAmount;
      updatedCurrentValues["Buydown spread"] = buydownSpread;

      // Don't override user's current input - preserve what they're typing
      // Only update if the field isn't currently being typed in
      const userInputFields = [
        "Monthly rent",
        "Annual taxes",
        "Annual insurance",
        "Annual HOA",
        "Purchase price",
        "Loan amount",
        "Estimated AS-IS value",
      ];

      userInputFields.forEach((fieldName) => {
        // Only update from answers if not currently being typed
        if (updatedCurrentValues[fieldName] === undefined) {
          const answerValue = updatedAnswers.find(
            (a) => a.q_title === fieldName
          )?.a_string;
          if (answerValue) {
            updatedCurrentValues[fieldName] = answerValue;
          }
        }

        // also make sure current field is updated to latest
        updatedCurrentValues[answer.q_title] = value;
      });

      // Special handling: If LTV or property values changed, ensure loan amount is updated from calculations
      if (
        answer.q_title === "Loan to value (LTV)" ||
        answer.q_title === "Purchase price" ||
        answer.q_title === "Estimated AS-IS value"
      ) {
        const loanAmountFromAnswers = updatedAnswers.find(
          (a) => a.q_title === "Loan amount"
        )?.a_string;
        if (loanAmountFromAnswers) {
          updatedCurrentValues["Loan amount"] = loanAmountFromAnswers;
        }
      }

      // Also ensure calculated loan amount from default logic is included
      if (loanAmount) {
        updatedCurrentValues["Loan amount"] = loanAmount;
      }

      // Then call setCurrentValues with the already-prepared object
      setCurrentValues(updatedCurrentValues);
    } else {
      updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0].a_error = true;
    }

    // Update all calculated fields in updatedAnswers from updatedCurrentValues

    Object.keys(updatedCurrentValues).forEach((fieldName) => {
      const correspondingAnswer = updatedAnswers.find(
        (answer) => answer.q_title === fieldName
      );
      if (correspondingAnswer) {
        correspondingAnswer.a_string =
          updatedCurrentValues[fieldName].toString();
      }
    });

    setAnswers(updatedAnswers);
  };

  // Debounced handler for processing user selections
  const debouncedHandleUserSelection = useCallback(
    (value, answer, answers, questions, setAnswers) => {
      // Clear any existing timeout for this field
      if (debounceTimeoutRef.current[answer.q_title]) {
        clearTimeout(debounceTimeoutRef.current[answer.q_title]);
      }

      // Set up debounced call
      debounceTimeoutRef.current[answer.q_title] = setTimeout(() => {
        handleUserSelection(
          value,
          answer,
          answers,
          questions,
          setAnswers,
          true
        ); // isUserInteraction = true
      }, 1000); // Wait 1 second after user stops typing
    },
    [handleUserSelection]
  );

  const resetCalculator = () => {
    // Loop through all answers and set a_string to ""

    const updatedAnswers = answers.map((answer) => ({
      ...answer,
      a_string: "",
    }));

    // Reset current values to ""
    const updatedCurrentValues = Object.keys(currentValues).reduce(
      (acc, key) => {
        acc[key] = ""; // Set each current value to an empty string
        return acc;
      },
      {}
    );

    setCurrentValues(updatedCurrentValues); // Update current values in state
    setAnswers(updatedAnswers); // Update answers in state

    // Reset change tracking (unified)
    setValueChanges({});
    originalValuesRef.current = {};
    hasUserInteractedInLoanTermsUIRef.current = false;
    initialLoadValuesRef.current = {};

    // Clear any pending debounce timeouts
    Object.values(debounceTimeoutRef.current).forEach(clearTimeout);
    debounceTimeoutRef.current = {};
  };

  const resetDiffs = () => {
    // Clear existing baselines
    originalValuesRef.current = {};

    // Seed with current user-visible values
    if (currentValues && typeof currentValues === "object") {
      Object.entries(currentValues).forEach(([key, value]) => {
        originalValuesRef.current[key] = value;
      });
    }

    // Seed calculated baselines so diffs start fresh immediately
    originalValuesRef.current.dscr = dscr;
    originalValuesRef.current.monthlyCashflow = monthlyCashflow;
    originalValuesRef.current.cashToClose = cashToClose;
    originalValuesRef.current.liquidityToVerify = liquidityToVerify;
    originalValuesRef.current["Monthly payment"] = monthlyPitia;

    // Clear displayed diffs
    setValueChanges({});
  };

  const toggleAllDescriptions = () => {
    const newState = !showAllDescriptions;
    setShowAllDescriptions(newState);

    // Get all question titles from the answers
    const allQuestionTitles = answers.map((answer) => answer.q_title);

    // Create updated state with all questions set to the new state
    const updatedShowQuestionDescription = allQuestionTitles.reduce(
      (acc, title) => {
        acc[title] = newState;
        return acc;
      },
      {} // Start with empty object
    );

    // Also include the special sections
    // UPDATE: dont include actually
    updatedShowQuestionDescription["Monthly payment"] = newState;
    // updatedShowQuestionDescription["Total loan"] = newState;
    // updatedShowQuestionDescription["DSCR"] = newState;
    // updatedShowQuestionDescription["Cash flow and cash to close"] = newState;
    // updatedShowQuestionDescription["Monthly cashflow"] = newState;
    // updatedShowQuestionDescription["Cash to close"] = newState;

    setShowQuestionDescription(updatedShowQuestionDescription);
  };

  const generateAnswerOptions = (answer, index, answers) => {
    let nonEditable = false;

    // Non-editable fields for non-admin users
    if (
      loanTermsUI &&
      !isOMTeam &&
      [
        "5 year treasury",
        "Credit spread",
        "Interest rate",
        "Origination fee",
        "Loan fees",
        "Title fees",
        "Escrow",
        "Prepaid interest",
        "Prepaid insurance",
        "Payment reserve",
        "Buydown spread",
      ].includes(answer.q_title)
    ) {
      nonEditable = true;
    }

    // Always non-editable fields in loanTermsUI
    if (
      loanTermsUI &&
      [
        "Loan purpose", // always non-editable after creation for loantermsui
        "Buydown amount", // always because calculated, UPDATE, apparently this can change now, so needs to be editable by admins
        // "Buydown spread", // always because calculated UPDATE, apparently this can change now, so needs to be editable by admins
        // "Prepayment spread", // always because calculated UPDATE, apparently this can change now, so needs to be editable by admins
      ].includes(answer.q_title)
    ) {
      nonEditable = true;
    }

    const questionsSlice = questions.slice();

    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string || null
            : currentValues[answer.q_title];

        // Round percent values to nearest tenth for display
        if (localValue && !isNaN(parseFloat(localValue))) {
          localValue = (
            Math.round(parseFloat(localValue) * 100) / 100
          ).toString();
        }

        if (answer.q_title === "5 year treasury") {
          // Use API data if available, otherwise fall back to answer.a_string
          localValue = loanQuoteData?.treasury5yr?.rate
            ? loanQuoteData?.treasury5yr?.rate
            : answer.a_string;

          // Round treasury rate to nearest tenth for display
          if (localValue && !isNaN(parseFloat(localValue))) {
            localValue = (
              Math.round(parseFloat(localValue) * 100) / 100
            ).toString();
          }
        }

        const percentValueChange = valueChanges[answer.q_title];

        // Special diff calculation for 5 year treasury
        let treasurySpecialDiff = null;
        if (
          answer.q_title === "5 year treasury" &&
          loanQuoteData?.treasury5yr?.rate &&
          externalCalc?.Answers
        ) {
          const savedTreasuryValue = externalCalc.Answers.find(
            (a) => a.q_title === "5 year treasury"
          )?.a_string;

          if (savedTreasuryValue) {
            treasurySpecialDiff =
              parseFloat(loanQuoteData.treasury5yr.rate) -
              parseFloat(savedTreasuryValue);
          }
        }

        const percentChangeDisplay =
          loanTermsUI && !userHasChangedValuesInLoanTermsUI ? (
            versionDifferences[answer.q_title] && (
              <Typography
                sx={{
                  position: "absolute",
                  right: theme.spacing(2),
                  top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                  transform: "translateY(-50%)",
                  color:
                    versionDifferences[answer.q_title].difference > 0
                      ? "success.main"
                      : "error.main",
                  fontSize: theme.spacing(1.8),
                  fontWeight: 600,
                  zIndex: 1,
                  pointerEvents: "none",
                  [theme.breakpoints.down("md")]: {
                    fontSize: theme.spacing(1.2),
                  },
                }}
              >
                {versionDifferences[answer.q_title].difference > 0 ? "+" : ""}
                {versionDifferences[answer.q_title].difference.toFixed(2)}%
              </Typography>
            )
          ) : treasurySpecialDiff !== null &&
            Math.abs(treasurySpecialDiff) > 0.01 ? (
            <Typography
              sx={{
                position: "absolute",
                right: theme.spacing(2),
                top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                transform: "translateY(-50%)",
                color: treasurySpecialDiff > 0 ? "error.main" : "success.main", // Positive diff = red, negative diff = green
                fontSize: theme.spacing(1.8),
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
                [theme.breakpoints.down("md")]: {
                  fontSize: theme.spacing(1.2),
                },
              }}
            >
              {treasurySpecialDiff > 0 ? "+" : ""}
              {treasurySpecialDiff.toFixed(2)}%
            </Typography>
          ) : percentValueChange && answer.q_title === "Credit spread" ? (
            <Typography
              sx={{
                position: "absolute",
                right: {
                  xs: theme.spacing(1), // Balanced position on mobile
                  md: theme.spacing(1.5), // Balanced position on desktop
                },
                top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                transform: "translateY(-50%)",
                color: percentValueChange > 0 ? "error.main" : "success.main", // Inverted: higher spread = red, lower spread = green
                fontSize: {
                  xs: theme.spacing(1.3), // Good visibility on mobile
                  md: theme.spacing(1.7), // Good visibility on desktop
                },
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {percentValueChange > 0 ? "+" : ""}
              {typeof percentValueChange === "number"
                ? percentValueChange.toFixed(2)
                : percentValueChange}
              %
            </Typography>
          ) : percentValueChange && answer.q_title === "Prepayment spread" ? (
            <Typography
              sx={{
                position: "absolute",
                right: {
                  xs: theme.spacing(1), // Balanced position on mobile
                  md: theme.spacing(1.5), // Balanced position on desktop
                },
                top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                transform: "translateY(-50%)",
                color: percentValueChange > 0 ? "error.main" : "success.main", // Higher spread = red, lower spread = green
                fontSize: {
                  xs: theme.spacing(1.3), // Good visibility on mobile
                  md: theme.spacing(1.7), // Good visibility on desktop
                },
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {percentValueChange > 0 ? "+" : ""}
              {typeof percentValueChange === "number"
                ? percentValueChange.toFixed(2)
                : percentValueChange}
              %
            </Typography>
          ) : percentValueChange && answer.q_title === "Buydown spread" ? (
            <Typography
              sx={{
                position: "absolute",
                right: {
                  xs: theme.spacing(1), // Balanced position on mobile
                  md: theme.spacing(1.5), // Balanced position on desktop
                },
                top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                transform: "translateY(-50%)",
                color: percentValueChange < 0 ? "success.main" : "error.main", // Negative (higher buydown spread) = green, positive (lower) = red
                fontSize: {
                  xs: theme.spacing(1.3), // Good visibility on mobile
                  md: theme.spacing(1.7), // Good visibility on desktop
                },
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {percentValueChange > 0 ? "+" : ""}
              {typeof percentValueChange === "number"
                ? percentValueChange.toFixed(2)
                : percentValueChange}
              %
            </Typography>
          ) : percentValueChange && answer.q_title === "Interest rate" ? (
            <Typography
              sx={{
                position: "absolute",
                right: {
                  xs: theme.spacing(1), // Balanced position on mobile
                  md: theme.spacing(1.5), // Balanced position on desktop
                },
                top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                transform: "translateY(-50%)",
                color: percentValueChange > 0 ? "error.main" : "success.main", // Inverted: higher rate = red, lower rate = green
                fontSize: {
                  xs: theme.spacing(1.3), // Good visibility on mobile
                  md: theme.spacing(1.7), // Good visibility on desktop
                },
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {percentValueChange > 0 ? "+" : ""}
              {typeof percentValueChange === "number"
                ? percentValueChange.toFixed(2)
                : percentValueChange}
              %
            </Typography>
          ) : percentValueChange && answer.q_title === "5 year treasury" ? (
            <Typography
              sx={{
                position: "absolute",
                right: {
                  xs: theme.spacing(1), // Balanced position on mobile
                  md: theme.spacing(1.5), // Balanced position on desktop
                },
                top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                transform: "translateY(-50%)",
                color: percentValueChange > 0 ? "error.main" : "success.main", // 5 year treasury: positive = red, negative = green
                fontSize: {
                  xs: theme.spacing(1.3), // Good visibility on mobile
                  md: theme.spacing(1.7), // Good visibility on desktop
                },
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {percentValueChange > 0 ? "+" : ""}
              {typeof percentValueChange === "number"
                ? percentValueChange.toFixed(2)
                : percentValueChange}
              %
            </Typography>
          ) : percentValueChange ? (
            <Typography
              sx={{
                position: "absolute",
                right: {
                  xs: theme.spacing(1), // Balanced position on mobile
                  md: theme.spacing(1.5), // Balanced position on desktop
                },
                // top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                bottom: answer.a_error
                  ? "50%"
                  : `calc(50% - ${theme.spacing(2.5)})`, // Adjust for error state
                transform: "translateY(-50%)",
                color: percentValueChange > 0 ? "success.main" : "error.main",
                fontSize: {
                  xs: theme.spacing(1.3), // Good visibility on mobile
                  md: theme.spacing(1.7), // Good visibility on desktop
                },
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {percentValueChange > 0 ? "+" : ""}
              {typeof percentValueChange === "number"
                ? percentValueChange.toFixed(2)
                : percentValueChange}
              %
            </Typography>
          ) : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
              position: "relative",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              readOnly={nonEditable}
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  paddingRight: percentValueChange ? "0px" : theme.spacing(2),
                  fontSize: {
                    xs: theme.spacing(2.2), // Larger font on mobile for main value
                    md: "inherit", // Standard size on desktop
                  },
                  ...(nonEditable && {
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(21, 101, 192, 0.04)", // Subtle hover effect
                    },
                  }),
                },
                // High specificity styles for non-editable fields
                ...(nonEditable && {
                  "& .MuiInputBase-root": {
                    color: "#1565c0 !important", // Bright blue for non-editable fields
                    fontWeight: "600 !important",
                    "& input": {
                      color: "#1565c0 !important",
                      fontWeight: "600 !important",
                      "-webkit-text-fill-color": "#1565c0 !important", // Override webkit autofill
                      cursor: "pointer",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#1565c0 !important",
                    fontWeight: "500 !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1565c0 !important",
                    borderWidth: "2px !important",
                  },
                }),
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              // Special handling for controlled percent fields
              {...([
                "5 year treasury",
                "Credit spread",
                "Interest rate",
                "Prepayment spread",
                "Buydown spread",
                "Loan to value (LTV)",
              ].includes(answer.q_title)
                ? {
                    value: localValue || "",
                    onChange: nonEditable
                      ? undefined
                      : (e) => {
                          const num = e.target.value;

                          const updatedCurrentValues = Object.assign(
                            {},
                            currentValues
                          );
                          updatedCurrentValues[answer.q_title] = num;
                          setCurrentValues(updatedCurrentValues);

                          debouncedHandleUserSelection(
                            num,
                            answer,
                            answers,
                            questions,
                            setAnswers
                          );
                        },
                  }
                : {
                    defaultValue: localValue || "",
                    onChange: nonEditable
                      ? undefined
                      : (e) => {
                          const num = e.target.value;

                          const updatedCurrentValues = Object.assign(
                            {},
                            currentValues
                          );
                          updatedCurrentValues[answer.q_title] = num;
                          setCurrentValues(updatedCurrentValues);

                          debouncedHandleUserSelection(
                            num,
                            answer,
                            answers,
                            questions,
                            setAnswers
                          );
                        },
                  })}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{
                      marginRight: percentValueChange ? theme.spacing(8) : 0,
                    }}
                  >
                    %
                  </InputAdornment>
                ),
              }}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text(
                      localValue,
                      answer,
                      loanTermsUI ? loan : {}
                    )
                  : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
              onClick={
                nonEditable
                  ? () => {
                      // Toggle the question description when clicking on nonEditable field
                      const updatedShowQuestionDescription = {
                        ...showQuestionDescription,
                        [answer.q_title]:
                          !showQuestionDescription[answer.q_title],
                      };
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }
                  : undefined
              }
            />
            {percentChangeDisplay}
          </Box>
        );
        break;
      case "mc":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              flex: "0 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {answer
              .a_opts(answers)
              .filter((a_opt) =>
                nonEditable ? answer.a_string === a_opt : true
              )
              .map((a_opt, index) => {
                {
                  return (
                    <Button
                      id={
                        index === 0
                          ? `text_field_${answer.q_title}_${
                              calc?.id ? calc.id : 0
                            }`
                          : `text_field_${answer.q_title}_opt_${index}`
                      }
                      sx={{ margin: theme.spacing(1) }}
                      variant={
                        answer.a_string === a_opt ? "contained" : "outlined"
                      }
                      readOnly={nonEditable}
                      onClick={
                        nonEditable
                          ? undefined
                          : (e) => {
                              handleUserSelection(
                                a_opt,
                                answer,
                                answers,
                                questions,
                                setAnswers
                              );
                            }
                      }
                      onKeyDown={
                        nonEditable
                          ? undefined
                          : (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();

                                handleUserSelection(
                                  a_opt,
                                  answer,
                                  answers,
                                  questions,
                                  setAnswers
                                );

                                const next_q_id = answer.q_next_step_func(
                                  answer,
                                  answers,
                                  questions
                                );

                                if (next_q_id) {
                                  const nextQuestion = questions.find(
                                    (q) => q.q_id === next_q_id
                                  );

                                  const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                                  const nextQuestionTextField =
                                    document.getElementById(
                                      nextQuestionTextFieldId
                                    );

                                  if (nextQuestionTextField) {
                                    nextQuestionTextField.focus();
                                  }
                                }
                              }
                            }
                      }
                    >
                      {a_opt}
                    </Button>
                  );
                }
              })}
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string || null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              readOnly={nonEditable}
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                margin: theme.spacing(5, 0),
                // High specificity styles for non-editable fields
                ...(nonEditable && {
                  "& .MuiInputBase-root": {
                    color: "#1565c0 !important", // Bright blue for non-editable fields
                    fontWeight: "600 !important",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(21, 101, 192, 0.04)", // Subtle hover effect
                    },
                    "& input": {
                      color: "#1565c0 !important",
                      fontWeight: "600 !important",
                      "-webkit-text-fill-color": "#1565c0 !important", // Override webkit autofill
                      cursor: "pointer",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#1565c0 !important",
                    fontWeight: "500 !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1565c0 !important",
                    borderWidth: "2px !important",
                  },
                }),
              }}
              error={answer.a_error}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              value={localTextValue || ""}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;

                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);

                debouncedHandleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text(
                      localTextValue,
                      answer,
                      loanTermsUI ? loan : {}
                    )
                  : ""
              }
            />
          </Box>
        );
        break;
      case "currency":
        const uiValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? `$${formatMoney(answer.a_string, 0)}`
              : null
            : !isNaN(currentValues[answer.q_title])
            ? `$${formatMoney(currentValues[answer.q_title], 0)}`
            : null;

        const valueChange = valueChanges[answer.q_title];

        const changeDisplay =
          loanTermsUI && !userHasChangedValuesInLoanTermsUI ? (
            versionDifferences[answer.q_title] && (
              <Typography
                sx={{
                  position: "absolute",
                  right: theme.spacing(2),
                  top: "50%", // Always center on input field
                  transform: "translateY(-50%)",
                  color:
                    answer.q_title === "Buydown amount"
                      ? versionDifferences[answer.q_title].difference > 0
                        ? "error.main"
                        : "success.main" // Buydown amount: positive = red, negative = green
                      : versionDifferences[answer.q_title].difference > 0
                      ? "success.main"
                      : "error.main", // Default: positive = green, negative = red
                  fontSize: theme.spacing(1.8),
                  fontWeight: 600,
                  zIndex: 1,
                  pointerEvents: "none",
                  [theme.breakpoints.down("md")]: {
                    fontSize: theme.spacing(1.2),
                  },
                }}
              >
                {versionDifferences[answer.q_title].difference > 0 ? "+" : "-"}$
                {formatMoney(
                  Math.abs(versionDifferences[answer.q_title].difference),
                  0
                )}
              </Typography>
            )
          ) : valueChange ? (
            <Typography
              sx={{
                position: "absolute",
                right: {
                  xs: theme.spacing(1), // Balanced position on mobile
                  md: theme.spacing(2), // Standard position on desktop
                },
                top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
                transform: "translateY(-50%)",
                color:
                  answer.q_title === "Buydown amount"
                    ? valueChange > 0
                      ? "error.main"
                      : "success.main" // Buydown amount: positive = red (more cost), negative = green (less cost)
                    : valueChange > 0
                    ? "success.main"
                    : "error.main", // Default: positive = green, negative = red
                fontSize: {
                  xs: theme.spacing(1.4), // Larger, more visible on mobile
                  md: theme.spacing(1.8), // Standard on desktop
                },
                fontWeight: 600,
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              {valueChange > 0 ? "+" : "-"}$
              {formatMoney(Math.abs(valueChange), 0)}
            </Typography>
          ) : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
              position: "relative",
            }}
          >
            <TextField
              readOnly={nonEditable}
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={answer.q_help_text ? answer.q_help_text : ""}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  paddingRight: valueChange
                    ? {
                        xs: theme.spacing(7), // Balanced space on mobile for both values
                        md: theme.spacing(8), // Standard space on desktop
                      }
                    : theme.spacing(2),
                  fontSize: {
                    xs: theme.spacing(2.2), // Larger font on mobile for main value
                    md: "inherit", // Standard size on desktop
                  },
                },
                // High specificity styles for non-editable fields
                ...(nonEditable && {
                  "& .MuiInputBase-root": {
                    color: "#1565c0 !important", // Bright blue for non-editable fields
                    fontWeight: "600 !important",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(21, 101, 192, 0.04)", // Subtle hover effect
                    },
                    "& input": {
                      color: "#1565c0 !important",
                      fontWeight: "600 !important",
                      "-webkit-text-fill-color": "#1565c0 !important", // Override webkit autofill
                      cursor: "pointer",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#1565c0 !important",
                    fontWeight: "500 !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1565c0 !important",
                    borderWidth: "2px !important",
                  },
                }),
              }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              inputProps={{
                inputMode: "numeric",
              }}
              value={uiValue || ""}
              onChange={
                nonEditable
                  ? undefined
                  : (e) => {
                      const num = e.target.value.replace(/\D/g, "");

                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = num;
                      setCurrentValues(updatedCurrentValues);

                      debouncedHandleUserSelection(
                        num,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );
                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );
                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );
                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
              onClick={
                nonEditable
                  ? () => {
                      // Toggle the question description when clicking on nonEditable field
                      const updatedShowQuestionDescription = {
                        ...showQuestionDescription,
                        [answer.q_title]:
                          !showQuestionDescription[answer.q_title],
                      };
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }
                  : undefined
              }
            />
            {changeDisplay}
          </Box>
        );
        break;
      case "number":
        let localNumberValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string || null
            : currentValues[answer.q_title];
        const numberValueChange = valueChanges[answer.q_title];

        const numberChangeDisplay = numberValueChange ? (
          <Typography
            sx={{
              position: "absolute",
              right: theme.spacing(2),
              top: answer.a_error ? "calc(50% - 12px)" : "50%", // Adjust for error state
              transform: "translateY(-50%)",
              color: numberValueChange > 0 ? "success.main" : "error.main",
              fontSize: theme.spacing(1.8),
              fontWeight: 600,
              zIndex: 1,
              pointerEvents: "none",
              [theme.breakpoints.down("md")]: {
                fontSize: theme.spacing(1.2),
              },
            }}
          >
            {numberValueChange > 0 ? "+" : ""}
            {typeof numberValueChange === "number"
              ? numberValueChange.toFixed(2)
              : numberValueChange}
          </Typography>
        ) : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
              position: "relative",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              readOnly={nonEditable}
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  paddingRight: numberValueChange
                    ? theme.spacing(8)
                    : theme.spacing(2),
                },
                // High specificity styles for non-editable fields
                ...(nonEditable && {
                  "& .MuiInputBase-root": {
                    color: "#1565c0 !important", // Bright blue for non-editable fields
                    fontWeight: "600 !important",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(21, 101, 192, 0.04)", // Subtle hover effect
                    },
                    "& input": {
                      color: "#1565c0 !important",
                      fontWeight: "600 !important",
                      "-webkit-text-fill-color": "#1565c0 !important", // Override webkit autofill
                      cursor: "pointer",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#1565c0 !important",
                    fontWeight: "500 !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1565c0 !important",
                    borderWidth: "2px !important",
                  },
                }),
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localNumberValue }}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              // Special handling for controlled number fields (like Buydown points)
              {...(answer.q_title === "Buydown points"
                ? {
                    value: localNumberValue || "",
                    onChange: nonEditable
                      ? undefined
                      : (e) => {
                          const num = e.target.value;

                          const updatedCurrentValues = Object.assign(
                            {},
                            currentValues
                          );
                          updatedCurrentValues[answer.q_title] = num;
                          setCurrentValues(updatedCurrentValues);

                          debouncedHandleUserSelection(
                            num,
                            answer,
                            answers,
                            questions,
                            setAnswers
                          );
                        },
                  }
                : {
                    value: localNumberValue || "",
                    onChange: nonEditable
                      ? undefined
                      : (e) => {
                          const value = e.target.value;

                          const updatedCurrentValues = Object.assign(
                            {},
                            currentValues
                          );
                          updatedCurrentValues[answer.q_title] = value;
                          setCurrentValues(updatedCurrentValues);

                          debouncedHandleUserSelection(
                            value,
                            answer,
                            answers,
                            questions,
                            setAnswers
                          );
                          // this is done to reset
                          // 'get rate' output when new stuff is typed in
                          setLoanQuoteData({});
                        },
                  })}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text(
                      localNumberValue,
                      answer,
                      loanTermsUI ? loan : {}
                    )
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
              onClick={
                nonEditable
                  ? () => {
                      // Toggle the question description when clicking on nonEditable field
                      const updatedShowQuestionDescription = {
                        ...showQuestionDescription,
                        [answer.q_title]:
                          !showQuestionDescription[answer.q_title],
                      };
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }
                  : undefined
              }
            />
            {numberChangeDisplay}
          </Box>
        );
        break;
      case "address":
        // Use answer.a_string first (for example calcs), fallback to calc.Address (for saved calcs)
        const address = answer.a_string || calc?.Address || "";

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              onAddressSelect={({ place, inputValue, source }) => {
                // Use place address if available, otherwise use raw input
                const selectedAddress = place?.formatted_address || inputValue;

                setSuggestionChoice(selectedAddress);

                handleUserSelection(
                  selectedAddress,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
                // NO auto-advance - calculator handles this differently
              }}
              initialAddress={address}
              label={answer.a_error ? "Error" : answer.q_title}
              ariaLabel="Enter address of the property for this loan"
              searchOptionsTypes={[]}
              error={answer.a_error}
            />
          </Box>
        );
        break;

      case "dropdown":
        const localDropdownValue = answer.a_string || "";

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              width: "100%",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <Box
              onClick={
                nonEditable
                  ? () => {
                      // Toggle the question description when clicking on non-editable field
                      const updatedShowQuestionDescription = {
                        ...showQuestionDescription,
                        [answer.q_title]:
                          !showQuestionDescription[answer.q_title],
                      };
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }
                  : undefined
              }
              sx={{
                cursor: nonEditable ? "pointer" : "default",
                "&:hover": nonEditable
                  ? {
                      "& .MuiFormControl-root": {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgba(21, 101, 192, 0.04)",
                        },
                      },
                    }
                  : {},
              }}
            >
              <FormControl
                fullWidth
                error={answer.a_error}
                disabled={nonEditable}
                sx={{
                  // High specificity styles for non-editable fields
                  ...(nonEditable && {
                    "& .MuiSelect-select": {
                      color: "#1565c0 !important", // Bright blue for non-editable fields
                      fontWeight: "600 !important",
                      "-webkit-text-fill-color": "#1565c0 !important", // Override webkit autofill
                    },
                    "& .MuiInputLabel-root": {
                      color: "#1565c0 !important",
                      fontWeight: "500 !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1565c0 !important",
                      borderWidth: "2px !important",
                    },
                  }),
                }}
              >
                <Select
                  id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
                  value={localDropdownValue}
                  displayEmpty
                  onChange={
                    nonEditable
                      ? undefined
                      : (e) => {
                          const selectedValue = e.target.value;

                          handleUserSelection(
                            selectedValue,
                            answer,
                            answers,
                            questions,
                            setAnswers
                          );
                        }
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const next_q_id = answer.q_next_step_func(
                        answer,
                        answers,
                        questions
                      );
                      if (next_q_id) {
                        const nextQuestion = questions.find(
                          (q) => q.q_id === next_q_id
                        );
                        const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                        const nextQuestionTextField = document.getElementById(
                          nextQuestionTextFieldId
                        );
                        if (nextQuestionTextField) {
                          nextQuestionTextField.focus();
                        }
                      }
                    }
                  }}
                  sx={{
                    width: "100%",
                    [theme.breakpoints.down("md")]: {
                      padding: `0 !important`,
                    },
                  }}
                >
                  <MenuItem value="" nonEditable>
                    <em>{answer.q_title}</em>
                  </MenuItem>
                  {(answer.q_title === "Prepayment penalty" &&
                  Object.keys(prepaymentOptions).length > 0
                    ? Object.keys(prepaymentOptions)
                    : answer.q_title === "Buydown points" &&
                      Object.keys(buydownOptions).length > 0
                    ? Object.keys(buydownOptions).sort(
                        (a, b) => parseFloat(b) - parseFloat(a)
                      ) // Sort descending (largest positive first)
                    : answer.a_opts(answers)
                  )
                    .filter((a_opt) =>
                      nonEditable ? answer.a_string === a_opt : true
                    )
                    .map((option, optionIndex) => (
                      <MenuItem
                        key={optionIndex}
                        value={option}
                        sx={{
                          textAlign: "center",
                          justifyContent: "center",
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                </Select>
                {answer.a_error && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, ml: 2 }}
                  >
                    {answer.q_invalid_answer_help_text(
                      localDropdownValue,
                      answer,
                      loanTermsUI ? loan : {}
                    )}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>
        );
        break;

      default:
    }

    return answerOptionsUI;
  };

  return (
    <Box
      key={`calculation_${externalCalc?.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {!loanTermsUI && (
          <Box
            key="left-controls"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={resetCalculator}
              sx={{
                width: "25%",
                margin: theme.spacing(1),
                [theme.breakpoints.down("md")]: {
                  width: "33%",
                },
                "@media print": { display: "none" },
              }}
              className="no-print-element"
            >
              Reset
            </Button>
          </Box>
        )}
        <Box
          key="right-controls"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={resetDiffs}
            sx={{
              margin: theme.spacing(1),
              "@media print": { display: "none" },
            }}
            className="no-print-element"
          >
            RESET DIFF
          </Button>
        </Box>
      </Box>

      {/* {loanTermsUI && calc?.RichExplanation && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            mb: 2,
            "& .MuiAlert-message": {
              width: "100%",
              textAlign: "left",
              "& p:first-of-type": {
                marginTop: 0,
              },
            },
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: calc.RichExplanation }} />
        </Alert>
      )} */}

      {router.pathname.includes("/my/calculation") &&
        loanPurpose &&
        cashToClose &&
        monthlyCashflow && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                margin: theme.spacing(0, "auto", 6, "auto"),
                [theme.breakpoints.down("md")]: {
                  width: "auto",
                  margin: theme.spacing(1, 1, 4, 1),
                  flexDirection: "column",
                  display: "flex",
                },
              }}
            >
              <Box
                sx={{
                  width: "30%",
                  display: "flex",
                  flexDirection: "column",
                  margin: theme.spacing(0, "auto", 6, "auto"),
                  [theme.breakpoints.down("md")]: {
                    width: "auto",
                    margin: theme.spacing(1, 1, 4, 1),
                  },
                }}
              >
                {loanPurpose && (
                  <Typography
                    sx={{
                      margin: theme.spacing(1),
                      fontSize: theme.spacing(2.5),
                      textAlign: "center",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    {`${loanPurpose} - $${formatMoney(
                      loanAmount,
                      0
                    )} - ${localInterestRate}%`}
                  </Typography>
                )}
                {monthlyCashflow && (
                  <Typography
                    sx={{
                      fontSize: theme.spacing(3),
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    Monthly cashflow:{" "}
                    <Typography
                      sx={{
                        color:
                          monthlyCashflow > 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        fontSize: theme.spacing(3),
                      }}
                      variant="span"
                    >
                      {" "}
                      {monthlyCashflow > 0 && "+"}
                      {`$${formatMoney(monthlyCashflow, 0)}`}
                    </Typography>
                  </Typography>
                )}
                {cashToClose && (
                  <Typography
                    sx={{
                      fontSize: theme.spacing(2.25),
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    {cashToClose < 0 ? "Cash proceeds" : "Cash to close"}:
                    <Typography
                      sx={{
                        color:
                          cashToClose > 0
                            ? theme.palette.primary.main
                            : theme.palette.success.main,
                        fontSize: theme.spacing(2.25),
                      }}
                      variant="span"
                    >
                      {cashToClose < 0 && " +"}
                      {` $${formatMoney(Math.abs(cashToClose), 0)}`}
                    </Typography>
                  </Typography>
                )}
                {cashToClose > 0 && monthlyCashflow && (
                  <Typography
                    sx={{
                      fontSize: theme.spacing(2.25),
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    {"Years to recoup"}:
                    <Typography
                      sx={{
                        color: theme.palette.success.main,
                        fontSize: theme.spacing(2.25),
                      }}
                      variant="span"
                    >
                      {(cashToClose / monthlyCashflow / 12).toFixed(2)}
                    </Typography>
                  </Typography>
                )}
              </Box>
              <Divider />
            </Box>
          </>
        )}

      {answers.length > 0 ? (
        <Box
          id="calculator-to-print"
          sx={{
            margin: theme.spacing(0, 0, 0, 0),
            borderRadius: theme.spacing(1),
            [theme.breakpoints.down("md")]: {
              padding: theme.spacing(1),
            },
          }}
        >
          <Box
            sx={{
              m: loanTermsUI
                ? {
                    xs: "0 2%", // Mobile: smaller margins
                    sm: "0 5%", // Tablet: medium margins
                    md: "0 15%", // Desktop: larger margins
                    lg: "0 25%", // Large desktop: largest margins
                  }
                : 0,
            }}
          >
            <Box id="pdf-4">
              <QuestionRenderer
                answers={answers}
                questions={firstPartOfQuestions}
                theme={theme}
                showQuestionDescription={showQuestionDescription}
                setShowQuestionDescription={setShowQuestionDescription}
                generateAnswerOptions={generateAnswerOptions}
                customContent={{
                  "5 year treasury": (
                    <InterestRateChart
                      showOnlyTreasury={true}
                      height={theme.spacing(31)}
                      loanCreatedAt={
                        loan?.created_at
                          ? dayjs(loan.created_at).format("YYYY-MM-DD")
                          : undefined
                      }
                    />
                  ),
                  "Loan to value (LTV)": (
                    <LTVQuoteTable
                      loanQuoteData={loanQuoteData}
                      propertyValue={propertyValue}
                      current5yrTreasury={current5yrTreasury}
                    />
                  ),
                  "Credit spread": (
                    <LTVQuoteTable
                      loanQuoteData={loanQuoteData}
                      propertyValue={propertyValue}
                      current5yrTreasury={current5yrTreasury}
                    />
                  ),
                  "Interest rate": (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        mb: 1,
                      }}
                    >
                      {(() => {
                        // Get current values from answers
                        const treasuryRate = parseFloat(
                          answers.find((a) => a.q_title === "5 year treasury")
                            ?.a_string || 0
                        );
                        const creditSpread = parseFloat(
                          answers.find((a) => a.q_title === "Credit spread")
                            ?.a_string || 0
                        );
                        const prepaymentSpread = parseFloat(
                          answers.find((a) => a.q_title === "Prepayment spread")
                            ?.a_string || 0
                        );
                        const buydownSpread = parseFloat(
                          answers.find((a) => a.q_title === "Buydown spread")
                            ?.a_string || 0
                        );
                        const interestRate = parseFloat(
                          answers.find((a) => a.q_title === "Interest rate")
                            ?.a_string || 0
                        );

                        return (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>Credit spread:</span>
                              <span>{creditSpread.toFixed(2)}%</span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>+ Prepayment spread:</span>
                              <span>{prepaymentSpread.toFixed(2)}%</span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>+ Buydown spread:</span>
                              <span>{buydownSpread.toFixed(2)}%</span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>+ 5 year treasury:</span>
                              <span>{treasuryRate.toFixed(2)}%</span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 1,
                                pt: 1,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                fontWeight: "bold",
                              }}
                            >
                              <span>Interest rate:</span>
                              <span>{interestRate.toFixed(2)}%</span>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  ),
                  "Monthly cashflow": (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        mb: 1,
                      }}
                    >
                      {(() => {
                        // Get current values
                        const monthlyRentValue = parseInt(monthlyRent) || 0;
                        const monthlyPmtValue = parseInt(monthlyPmt) || 0;
                        const annualTaxesValue = parseInt(annualTaxes) || 0;
                        const annualInsuranceValue =
                          parseInt(annualInsurance) || 0;
                        const annualHOAValue = parseInt(annualHOA) || 0;
                        const monthlyCashflowValue =
                          parseInt(monthlyCashflow) || 0;

                        return (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                                fontWeight: "bold",
                              }}
                            >
                              <span>Monthly rent:</span>
                              <span>${formatMoney(monthlyRentValue, 0)}</span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>- Monthly loan payment:</span>
                              <span>${formatMoney(monthlyPmtValue, 0)}</span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>- Prorated taxes:</span>
                              <span>
                                ${formatMoney(annualTaxesValue / 12, 0)}
                              </span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>- Prorated insurance:</span>
                              <span>
                                ${formatMoney(annualInsuranceValue / 12, 0)}
                              </span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span>- Monthly HOAs (if any):</span>
                              <span>
                                ${formatMoney(annualHOAValue / 12, 0)}
                              </span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 1,
                                pt: 1,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                fontWeight: "bold",
                              }}
                            >
                              <span>Estimated monthly cashflow:</span>
                              <span>
                                ${formatMoney(monthlyCashflowValue, 0)}
                              </span>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  ),
                  "Cash to close": (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        mb: 1,
                      }}
                    >
                      {(() => {
                        // Get current values
                        const downpaymentValue = Math.abs(
                          parseInt(variableExpenseCashToClose) -
                            parseInt(loanAmount)
                        );
                        const originationFeeValue =
                          parseInt(originationFee) || 0;
                        const loanFeesValue = parseInt(loanFees) || 0;
                        const titleFeesValue = parseInt(titleFees) || 0;
                        const escrowValue = parseInt(escrow) || 0;
                        const prepaidInterestValue =
                          parseInt(prepaidInterest) || 0;
                        const prepaidInsuranceValue =
                          parseInt(prepaidInsurance) || 0;
                        const buydownAmountValue = parseInt(buydownAmount) || 0;
                        const cashToCloseValue =
                          parseInt(Math.abs(cashToClose)) || 0;

                        return (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                                fontWeight: "bold",
                              }}
                            >
                              <span>
                                {loanPurpose === "Refinance"
                                  ? "Proceeds:"
                                  : "Downpayment:"}
                              </span>
                              <span>${formatMoney(downpaymentValue, 0)}</span>
                            </Box>
                            {originationFeeValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>
                                  {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                                  Origination fee:
                                </span>
                                <span>
                                  ${formatMoney(originationFeeValue, 0)}
                                </span>
                              </Box>
                            )}
                            {loanFeesValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>
                                  {loanPurpose === "Refinance" ? "-" : "+"} Loan
                                  fees:
                                </span>
                                <span>${formatMoney(loanFeesValue, 0)}</span>
                              </Box>
                            )}
                            {titleFeesValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>
                                  {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                                  Title fees:
                                </span>
                                <span>${formatMoney(titleFeesValue, 0)}</span>
                              </Box>
                            )}
                            {escrowValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>
                                  {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                                  Escrow:
                                </span>
                                <span>${formatMoney(escrowValue, 0)}</span>
                              </Box>
                            )}
                            {prepaidInterestValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>
                                  {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                                  Prepaid interest:
                                </span>
                                <span>
                                  ${formatMoney(prepaidInterestValue, 0)}
                                </span>
                              </Box>
                            )}
                            {prepaidInsuranceValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>
                                  {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                                  Prepaid insurance:
                                </span>
                                <span>
                                  ${formatMoney(prepaidInsuranceValue, 0)}
                                </span>
                              </Box>
                            )}
                            {buydownAmountValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>
                                  {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                                  Buydown amount:
                                </span>
                                <span>
                                  ${formatMoney(buydownAmountValue, 0)}
                                </span>
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 1,
                                pt: 1,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                fontWeight: "bold",
                              }}
                            >
                              <span>
                                {loanPurpose === "Refinance"
                                  ? "Proceeds:"
                                  : "Cash to close:"}
                              </span>
                              <span>${formatMoney(cashToCloseValue, 0)}</span>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  ),
                  "Liquidity to verify": (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        mb: 1,
                      }}
                    >
                      {(() => {
                        // Get current values
                        const downpaymentValue = Math.abs(
                          parseInt(variableExpenseCashToClose) -
                            parseInt(loanAmount)
                        );
                        const originationFeeValue =
                          parseInt(originationFee) || 0;
                        const loanFeesValue = parseInt(loanFees) || 0;
                        const titleFeesValue = parseInt(titleFees) || 0;
                        const escrowValue = parseInt(escrow) || 0;
                        const prepaidInterestValue =
                          parseInt(prepaidInterest) || 0;
                        const prepaidInsuranceValue =
                          parseInt(prepaidInsurance) || 0;
                        const buydownAmountValue = parseInt(buydownAmount) || 0;
                        const pitiaReserveValue =
                          (parseInt(monthlyPmt) +
                            parseInt(annualTaxes) / 12 +
                            parseInt(annualInsurance) / 12 +
                            parseInt(annualHOA) / 12) *
                          pitiaReserveMonths;
                        const liquidityToVerifyValue =
                          Math.max(0, liquidityToVerify) || 0;

                        return (
                          <>
                            {loanPurpose === "Purchase" && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                  fontWeight: "bold",
                                }}
                              >
                                <span>Downpayment:</span>
                                <span>${formatMoney(downpaymentValue, 0)}</span>
                              </Box>
                            )}
                            {originationFeeValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Origination fee:</span>
                                <span>
                                  ${formatMoney(originationFeeValue, 0)}
                                </span>
                              </Box>
                            )}
                            {loanFeesValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Loan fees:</span>
                                <span>${formatMoney(loanFeesValue, 0)}</span>
                              </Box>
                            )}
                            {titleFeesValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Title fees:</span>
                                <span>${formatMoney(titleFeesValue, 0)}</span>
                              </Box>
                            )}
                            {escrowValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Escrow:</span>
                                <span>${formatMoney(escrowValue, 0)}</span>
                              </Box>
                            )}
                            {prepaidInterestValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Prepaid interest:</span>
                                <span>
                                  ${formatMoney(prepaidInterestValue, 0)}
                                </span>
                              </Box>
                            )}
                            {prepaidInsuranceValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Prepaid insurance:</span>
                                <span>
                                  ${formatMoney(prepaidInsuranceValue, 0)}
                                </span>
                              </Box>
                            )}
                            {buydownAmountValue > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Buydown amount:</span>
                                <span>
                                  ${formatMoney(buydownAmountValue, 0)}
                                </span>
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span style={{ textAlign: "left" }}>
                                + PITIA reserve ({pitiaReserveMonths} months):
                              </span>
                              <span>${formatMoney(pitiaReserveValue, 0)}</span>
                            </Box>
                            {loanPurpose === "Refinance" && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>- 50% of net proceeds:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    Math.abs(parseInt(cashToClose)) * 0.5,
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 1,
                                pt: 1,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                fontWeight: "bold",
                              }}
                            >
                              <span>Liquidity to verify:</span>
                              <span>
                                ${formatMoney(liquidityToVerifyValue, 0)}
                              </span>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  ),
                }}
                descriptionParams={loanTermsUI && loan ? loan : {}}
              />
            </Box>
            <Box
              id="pdf-5"
              sx={{
                position: "sticky",
                minHeight: {
                  xs: theme.spacing(16),
                  md: theme.spacing(14),
                },
                bottom: 0,
                width: "100%",
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.grey[300]}`,
                transition: "all 0.3s ease",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  marginTop: theme.spacing(0),
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Monthly payment section - moved to sticky area */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: 1,
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(0.5),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          const updatedShowQuestionDescription = {
                            ...showQuestionDescription,
                            "Monthly payment":
                              !showQuestionDescription["Monthly payment"],
                          };
                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(2.8),
                            textAlign: "left",
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(1.8),
                            },
                          }}
                        >
                          Monthly payment
                        </Typography>
                        {showQuestionDescription["Monthly payment"] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        margin: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        minHeight: theme.spacing(4),
                        width: "100%",
                        maxWidth: "200px",
                        overflow: "hidden",
                        [theme.breakpoints.down("sm")]: {
                          maxWidth: "100%",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontWeight: 600,
                          fontSize: theme.spacing(2),
                          position: "absolute",
                          textAlign: "center",
                          width: "100%",
                          transition:
                            "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                          transform:
                            Math.abs(valueChanges["Monthly payment"]) > 1
                              ? `translateX(-${theme.spacing(4)})`
                              : "translateX(0)",
                          [theme.breakpoints.down("sm")]: {
                            fontSize: theme.spacing(1.6),
                          },
                        }}
                      >
                        ${formatMoney(monthlyPitia, 0)}
                      </Box>
                      {/* Monthly payment diff indicator */}
                      <Typography
                        component="span"
                        sx={{
                          position: "absolute",
                          opacity: valueChanges["Monthly payment"] ? 1 : 0,
                          transition:
                            "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                          transform: valueChanges["Monthly payment"]
                            ? `translateX(${theme.spacing(4)})`
                            : "translateX(0)",
                          color:
                            valueChanges["Monthly payment"] > 0
                              ? "error.main"
                              : "success.main", // Higher payment = red, lower payment = green
                          fontSize: theme.spacing(1.8),
                          fontWeight: 600,
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(1.2),
                          },
                        }}
                      >
                        {valueChanges["Monthly payment"] &&
                        Math.abs(valueChanges["Monthly payment"]) >= 1
                          ? `${
                              valueChanges["Monthly payment"] > 0 ? "+" : "-"
                            }$${formatMoney(
                              Math.abs(valueChanges["Monthly payment"]),
                              0
                            )}`
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription["Monthly payment"]}>
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          margin: theme.spacing(1),
                          fontSize: theme.spacing(1.8),
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1, 0, 1, 1),
                          },
                        }}
                      >
                        Monthly payment that will cover loan's principal,
                        interest, taxes, insurance, and HOA (PITIA)
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    ...(calculatorMode === "DSCR" && {
                      backgroundColor: "#e3f2fd",
                      border: "3px solid #1976d2",
                      borderRadius: theme.spacing(1),
                      padding: 0,
                      margin: theme.spacing(0.5, 0),
                      boxShadow: "0 4px 8px rgba(25, 118, 210, 0.2)",
                    }),
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <ButtonBase
                      sx={{
                        width: "100%",
                        padding: theme.spacing(1),
                        "&:focus": {
                          outline: "none",
                          borderRadius: theme.spacing(0.5),
                          boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                            theme.palette.info.main
                          }`,
                        },
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(0.5),
                          padding: 0,
                        },
                      }}
                      onClick={() => {
                        const updatedShowQuestionDescription = {
                          ...showQuestionDescription,
                          DSCR: !showQuestionDescription["DSCR"],
                          "Monthly cashflow": false,
                          "Cash to close": false,
                        };

                        setShowQuestionDescription(
                          updatedShowQuestionDescription
                        );
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 600,
                          fontSize: theme.spacing(2.8),
                          textAlign: "left",

                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(1.8),
                          },
                        }}
                      >
                        DSCR
                      </Typography>
                      {showQuestionDescription["DSCR"] ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                          color="info"
                        />
                      )}
                    </ButtonBase>
                  </Box>
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      margin: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      minHeight: theme.spacing(4),
                      width: "100%",
                      maxWidth: "200px",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      sx={{
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(1.6),
                        },
                        fontWeight: 600,
                        fontSize: theme.spacing(2),
                        textAlign: "center",
                        width: "100%",
                        color:
                          dscr.toFixed(2) > 1.09
                            ? theme.palette.success.main
                            : theme.palette.secondary.main,
                        position: "absolute",
                        transition:
                          "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform:
                          (loanTermsUI &&
                            !userHasChangedValuesInLoanTermsUI &&
                            calculatedValueDifferences.dscr &&
                            Math.abs(
                              calculatedValueDifferences.dscr.difference
                            ) > 0.01) ||
                          ((!loanTermsUI ||
                            (loanTermsUI &&
                              userHasChangedValuesInLoanTermsUI)) &&
                            valueChanges.dscr &&
                            Math.abs(valueChanges.dscr) > 0.01)
                            ? `translateX(-${theme.spacing(4)})`
                            : "translateX(0)",
                      }}
                    >
                      {!isNaN(dscr) && dscr !== null && dscr !== undefined
                        ? dscr.toFixed(2)
                        : ""}
                    </Typography>

                    {/* Always present difference indicator */}
                    <Typography
                      component="span"
                      sx={{
                        position: "absolute",
                        opacity:
                          (loanTermsUI &&
                            !userHasChangedValuesInLoanTermsUI &&
                            calculatedValueDifferences.dscr &&
                            Math.abs(
                              calculatedValueDifferences.dscr.difference
                            ) > 0.01) ||
                          ((!loanTermsUI ||
                            (loanTermsUI &&
                              userHasChangedValuesInLoanTermsUI)) &&
                            valueChanges.dscr &&
                            Math.abs(valueChanges.dscr) > 0.01)
                            ? 1
                            : 0,
                        transition:
                          "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform:
                          (loanTermsUI &&
                            !userHasChangedValuesInLoanTermsUI &&
                            calculatedValueDifferences.dscr &&
                            Math.abs(
                              calculatedValueDifferences.dscr.difference
                            ) > 0.01) ||
                          ((!loanTermsUI ||
                            (loanTermsUI &&
                              userHasChangedValuesInLoanTermsUI)) &&
                            valueChanges.dscr &&
                            Math.abs(valueChanges.dscr) > 0.01)
                            ? `translateX(${theme.spacing(4)})`
                            : "translateX(0)",
                        color:
                          loanTermsUI &&
                          !userHasChangedValuesInLoanTermsUI &&
                          calculatedValueDifferences.dscr
                            ? calculatedValueDifferences.dscr.difference > 0
                              ? "success.main"
                              : "error.main"
                            : valueChanges.dscr > 0
                            ? "success.main"
                            : "error.main",
                        fontSize: theme.spacing(1.8),
                        fontWeight: 600,
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(1.2),
                        },
                      }}
                    >
                      {loanTermsUI &&
                      !userHasChangedValuesInLoanTermsUI &&
                      calculatedValueDifferences.dscr
                        ? `${
                            calculatedValueDifferences.dscr.difference > 0
                              ? "+"
                              : ""
                          }${calculatedValueDifferences.dscr.difference.toFixed(
                            2
                          )}`
                        : valueChanges.dscr
                        ? `${valueChanges.dscr > 0 ? "+" : ""}${
                            valueChanges.dscr.toFixed
                              ? valueChanges.dscr.toFixed(2)
                              : valueChanges.dscr
                          }`
                        : "+0.00"}
                    </Typography>
                  </Box>
                </Box>
                <Collapse in={showQuestionDescription["DSCR"]}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: theme.spacing(1),
                        fontSize: theme.spacing(1.8),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 0, 1, 1),
                        },
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <strong>DSCR (Debt Service Coverage Ratio)</strong> is
                        calculated as:
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                            fontWeight: "bold",
                          }}
                        >
                          <span>Annual rental income:</span>
                          <span>${formatMoney(annualRent, 0)}</span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>Annual mortage service:</span>
                          <span>${formatMoney(monthlyPmt * 12, 0)}</span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>Annual taxes:</span>
                          <span>${formatMoney(annualTaxes, 0)}</span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>Annual insurance:</span>
                          <span>${formatMoney(annualInsurance, 0)}</span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>Annual HOA:</span>
                          <span>${formatMoney(annualHOA, 0)}</span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                            fontWeight: "bold",
                          }}
                        >
                          <span>Total annual debt service:</span>
                          <span>
                            $
                            {formatMoney(
                              monthlyPmt * 12 +
                                parseInt(annualTaxes) +
                                parseInt(annualInsurance) +
                                parseInt(annualHOA),
                              0
                            )}
                          </span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 1,
                            pt: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            fontWeight: "bold",
                          }}
                        >
                          <span>DSCR</span>
                          <span>
                            ${formatMoney(annualRent, 0)} ÷ $
                            {formatMoney(
                              monthlyPmt * 12 +
                                parseInt(annualTaxes) +
                                parseInt(annualInsurance) +
                                parseInt(annualHOA),
                              0
                            )}{" "}
                            ={" "}
                            <span
                              style={{
                                color:
                                  dscr >= 1.1
                                    ? theme.palette.success.main
                                    : theme.palette.secondary.main,
                              }}
                            >
                              {dscr.toFixed(2)}
                            </span>
                          </span>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        OfferMarket's minimum DSCR is 1.1. A DSCR above 1.1
                        indicates that the property generates enough income to
                        cover its debt obligations.
                      </Box>
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
              <Box
                id="pdf-6"
                tabindex={0}
                sx={{
                  zIndex: 2,
                  "&:focus": {
                    outline: "none",
                    borderRadius: theme.spacing(0.5),
                    boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                      theme.palette.success.main
                    }`,
                  },
                  ...(calculatorMode === "DSCR loan" &&
                    !loanTermsUI && {
                      backgroundColor: "#e8f5e8",
                      border: "3px solid #2e7d32",
                      borderRadius: theme.spacing(1),
                      padding: 0,
                      margin: theme.spacing(0.5, 0),
                      boxShadow: "0 4px 8px rgba(46, 125, 50, 0.2)",
                    }),
                }}
              >
                <Box
                  id="title_Monthly cashflow"
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(0.5),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          const updatedShowQuestionDescription = {
                            ...showQuestionDescription,
                            DSCR: false,
                            "Monthly cashflow":
                              !showQuestionDescription["Monthly cashflow"],
                            "Cash to close": false,
                          };

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(2.8),
                            textAlign: "left",
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(1.8),
                            },
                          }}
                        >
                          Monthly cashflow
                        </Typography>
                        {showQuestionDescription["Monthly cashflow"] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>

                    <Box
                      sx={{
                        flex: "0 1 50%",
                        margin: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        minHeight: theme.spacing(4),
                        width: "100%",
                        maxWidth: "200px",
                        overflow: "hidden",
                      }}
                    >
                      {userProfile && Object.keys(userProfile).length === 0 ? (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => setShowAuth(true)}
                          sx={{
                            px: 2,
                            py: 0.75,
                          }}
                        >
                          Sign in to view
                        </Button>
                      ) : (
                        <Typography
                          sx={{
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(1.6),
                            },
                            fontWeight: 600,
                            fontSize: theme.spacing(2),
                            textAlign: "center",
                            width: "100%",
                            color:
                              monthlyCashflow > -1
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                            position: "absolute",
                            transition:
                              "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform:
                              (loanTermsUI &&
                                !userHasChangedValuesInLoanTermsUI &&
                                calculatedValueDifferences.monthlyCashflow &&
                                Math.abs(
                                  calculatedValueDifferences.monthlyCashflow
                                    .difference
                                ) > 0.01) ||
                              ((!loanTermsUI ||
                                (loanTermsUI &&
                                  userHasChangedValuesInLoanTermsUI)) &&
                                valueChanges.monthlyCashflow &&
                                Math.abs(valueChanges.monthlyCashflow) > 0.01)
                                ? `translateX(-${theme.spacing(4)})`
                                : "translateX(0)",
                          }}
                        >
                          {`$${formatMoney(monthlyCashflow, 0)}`}
                        </Typography>
                      )}

                      {/* Always present difference indicator */}
                      {!(
                        userProfile && Object.keys(userProfile).length === 0
                      ) && (
                        <Typography
                          component="span"
                          sx={{
                            position: "absolute",
                            opacity:
                              (loanTermsUI &&
                                !userHasChangedValuesInLoanTermsUI &&
                                calculatedValueDifferences.monthlyCashflow &&
                                Math.abs(
                                  calculatedValueDifferences.monthlyCashflow
                                    .difference
                                ) > 0.01) ||
                              ((!loanTermsUI ||
                                (loanTermsUI &&
                                  userHasChangedValuesInLoanTermsUI)) &&
                                valueChanges.monthlyCashflow &&
                                Math.abs(valueChanges.monthlyCashflow) > 0.01)
                                ? 1
                                : 0,
                            transition:
                              "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform:
                              (loanTermsUI &&
                                !userHasChangedValuesInLoanTermsUI &&
                                calculatedValueDifferences.monthlyCashflow &&
                                Math.abs(
                                  calculatedValueDifferences.monthlyCashflow
                                    .difference
                                ) > 0.01) ||
                              ((!loanTermsUI ||
                                (loanTermsUI &&
                                  userHasChangedValuesInLoanTermsUI)) &&
                                valueChanges.monthlyCashflow &&
                                Math.abs(valueChanges.monthlyCashflow) > 0.01)
                                ? `translateX(${theme.spacing(4)})`
                                : "translateX(0)",
                            color:
                              loanTermsUI &&
                              !userHasChangedValuesInLoanTermsUI &&
                              calculatedValueDifferences.monthlyCashflow
                                ? calculatedValueDifferences.monthlyCashflow
                                    .difference > 0
                                  ? "success.main"
                                  : "error.main"
                                : valueChanges.monthlyCashflow > 0
                                ? "success.main"
                                : "error.main",
                            fontSize: theme.spacing(1.8),
                            fontWeight: 600,
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(1.2),
                            },
                          }}
                        >
                          {loanTermsUI &&
                          !userHasChangedValuesInLoanTermsUI &&
                          calculatedValueDifferences.monthlyCashflow
                            ? `${
                                calculatedValueDifferences.monthlyCashflow
                                  .difference > 0
                                  ? "+"
                                  : "-"
                              }$${formatMoney(
                                Math.abs(
                                  calculatedValueDifferences.monthlyCashflow
                                    .difference
                                ),
                                0
                              )}`
                            : valueChanges.monthlyCashflow
                            ? `${
                                valueChanges.monthlyCashflow > 0 ? "+" : "-"
                              }$${formatMoney(
                                Math.abs(valueChanges.monthlyCashflow),
                                0
                              )}`
                            : "+$0"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription["Monthly cashflow"]}>
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.paper,
                          m: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                            fontWeight: "bold",
                          }}
                        >
                          <span>Monthly rent:</span>
                          <span>${formatMoney(parseInt(monthlyRent), 0)}</span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>- Monthly loan payment:</span>
                          <span>${formatMoney(parseInt(monthlyPmt), 0)}</span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>- Prorated taxes:</span>
                          <span>
                            ${formatMoney(parseInt(annualTaxes) / 12, 0)}
                          </span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>- Prorated insurance:</span>
                          <span>
                            ${formatMoney(parseInt(annualInsurance) / 12, 0)}
                          </span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <span>- Monthly HOAs (if any):</span>
                          <span>
                            ${formatMoney(parseInt(annualHOA) / 12, 0)}
                          </span>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 1,
                            pt: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            fontWeight: "bold",
                          }}
                        >
                          <span>Estimated monthly cashflow:</span>
                          <span>${formatMoney(monthlyCashflow, 0)}</span>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
              <Box
                id="pdf-8"
                tabindex={0}
                sx={{
                  zIndex: 2,
                  "&:focus": {
                    outline: "none",
                    borderRadius: theme.spacing(0.5),
                    boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                      theme.palette.success.main
                    }`,
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(0.5),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          const updatedShowQuestionDescription = {
                            ...showQuestionDescription,
                            DSCR: false,
                            "Monthly cashflow": false,
                            "Cash to close":
                              !showQuestionDescription["Cash to close"],
                          };

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(2.8),
                            textAlign: "left",
                            color: theme.palette.primary.dark,
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(1.8),
                              textAlign: "center",
                            },
                          }}
                        >
                          {loanPurpose === "Purchase"
                            ? "Cash to close"
                            : "Proceeds"}
                        </Typography>
                        {showQuestionDescription["Cash to close"] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>

                    <Box
                      sx={{
                        flex: "0 1 50%",
                        margin: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        minHeight: theme.spacing(4),
                        width: "100%",
                        maxWidth: "200px",
                        overflow: "hidden",
                        [theme.breakpoints.down("sm")]: {
                          maxWidth: "100%",
                        },
                      }}
                    >
                      {userProfile && Object.keys(userProfile).length === 0 ? (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => setShowAuth(true)}
                          sx={{
                            px: 2,
                            py: 0.75,
                          }}
                        >
                          Sign in to view
                        </Button>
                      ) : (
                        <Box
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(2),
                            textAlign: "center",
                            width: "100%",
                            color:
                              loanPurpose === "Refinance"
                                ? cashToClose < 0
                                  ? theme.palette.success.main
                                  : theme.palette.primary.main
                                : "inherit",
                            position: "absolute",
                            transition:
                              "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform:
                              (loanTermsUI &&
                                !userHasChangedValuesInLoanTermsUI &&
                                calculatedValueDifferences.cashToClose &&
                                Math.abs(
                                  calculatedValueDifferences.cashToClose
                                    .difference
                                ) > 0.01) ||
                              ((!loanTermsUI ||
                                (loanTermsUI &&
                                  userHasChangedValuesInLoanTermsUI)) &&
                                valueChanges.cashToClose &&
                                Math.abs(valueChanges.cashToClose) > 0.01)
                                ? `translateX(-${theme.spacing(5)})`
                                : "translateX(0)",
                            [theme.breakpoints.down("sm")]: {
                              fontSize: theme.spacing(1.6),
                            },
                          }}
                        >
                          {userProfile && Object.keys(userProfile).length === 0
                            ? ""
                            : loanPurpose === "Purchase"
                            ? `$${formatMoney(cashToClose, 0)}`
                            : `$${formatMoney(-1 * cashToClose, 0)}`}
                        </Box>
                      )}

                      {/* Always present difference indicator */}
                      {!(
                        userProfile && Object.keys(userProfile).length === 0
                      ) && (
                        <Typography
                          component="span"
                          sx={{
                            position: "absolute",
                            opacity:
                              (loanTermsUI &&
                                !userHasChangedValuesInLoanTermsUI &&
                                calculatedValueDifferences.cashToClose &&
                                Math.abs(
                                  calculatedValueDifferences.cashToClose
                                    .difference
                                ) > 0.01) ||
                              ((!loanTermsUI ||
                                (loanTermsUI &&
                                  userHasChangedValuesInLoanTermsUI)) &&
                                valueChanges.cashToClose &&
                                Math.abs(valueChanges.cashToClose) > 0.01)
                                ? 1
                                : 0,
                            transition:
                              "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform:
                              (loanTermsUI &&
                                !userHasChangedValuesInLoanTermsUI &&
                                calculatedValueDifferences.cashToClose &&
                                Math.abs(
                                  calculatedValueDifferences.cashToClose
                                    .difference
                                ) > 0.01) ||
                              ((!loanTermsUI ||
                                (loanTermsUI &&
                                  userHasChangedValuesInLoanTermsUI)) &&
                                valueChanges.cashToClose &&
                                Math.abs(valueChanges.cashToClose) > 0.01)
                                ? `translateX(${theme.spacing(5)})`
                                : "translateX(0)",
                            color:
                              loanTermsUI &&
                              !userHasChangedValuesInLoanTermsUI &&
                              calculatedValueDifferences.cashToClose
                                ? calculatedValueDifferences.cashToClose
                                    .difference > 0
                                  ? "error.main"
                                  : "success.main"
                                : valueChanges.cashToClose > 0
                                ? "error.main"
                                : "success.main",
                            fontSize: theme.spacing(1.8),
                            fontWeight: 600,
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(1.2),
                            },
                          }}
                        >
                          {loanTermsUI &&
                          !userHasChangedValuesInLoanTermsUI &&
                          calculatedValueDifferences.cashToClose
                            ? `${
                                calculatedValueDifferences.cashToClose
                                  .difference > 0
                                  ? "+"
                                  : "-"
                              }$${formatMoney(
                                Math.abs(
                                  calculatedValueDifferences.cashToClose
                                    .difference
                                ),
                                0
                              )}`
                            : valueChanges.cashToClose
                            ? `${
                                valueChanges.cashToClose > 0 ? "+" : "-"
                              }$${formatMoney(
                                Math.abs(valueChanges.cashToClose),
                                0
                              )}`
                            : "+$0"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription["Cash to close"]}>
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.paper,
                          m: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                            fontWeight: "bold",
                          }}
                        >
                          <span>
                            {loanPurpose === "Refinance"
                              ? "Proceeds:"
                              : "Downpayment:"}
                          </span>
                          <span>
                            $
                            {formatMoney(
                              Math.abs(
                                parseInt(variableExpenseCashToClose) -
                                  parseInt(loanAmount)
                              ),
                              0
                            )}
                          </span>
                        </Box>
                        {parseInt(originationFee || 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <span>
                              {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                              Origination fee:
                            </span>
                            <span>
                              ${formatMoney(parseInt(originationFee || 0), 0)}
                            </span>
                          </Box>
                        )}
                        {parseInt(loanFees || 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <span>
                              {loanPurpose === "Refinance" ? "-" : "+"} Loan
                              fees:
                            </span>
                            <span>
                              ${formatMoney(parseInt(loanFees || 0), 0)}
                            </span>
                          </Box>
                        )}
                        {parseInt(titleFees || 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <span>
                              {loanPurpose === "Refinance" ? "-" : "+"} Title
                              fees:
                            </span>
                            <span>
                              ${formatMoney(parseInt(titleFees || 0), 0)}
                            </span>
                          </Box>
                        )}
                        {parseInt(escrow || 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <span>
                              {loanPurpose === "Refinance" ? "-" : "+"} Escrow:
                            </span>
                            <span>
                              ${formatMoney(parseInt(escrow || 0), 0)}
                            </span>
                          </Box>
                        )}
                        {parseInt(prepaidInterest || 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <span>
                              {loanPurpose === "Refinance" ? "-" : "+"} Prepaid
                              interest:
                            </span>
                            <span>
                              ${formatMoney(parseInt(prepaidInterest || 0), 0)}
                            </span>
                          </Box>
                        )}
                        {parseInt(prepaidInsurance || 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <span>
                              {loanPurpose === "Refinance" ? "-" : "+"} Prepaid
                              insurance:
                            </span>
                            <span>
                              ${formatMoney(parseInt(prepaidInsurance || 0), 0)}
                            </span>
                          </Box>
                        )}
                        {parseInt(buydownAmount || 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <span>
                              {loanPurpose === "Refinance" ? "-" : "+"} Buydown
                              amount:
                            </span>
                            <span>
                              ${formatMoney(parseInt(buydownAmount || 0), 0)}
                            </span>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 1,
                            pt: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            fontWeight: "bold",
                          }}
                        >
                          <span>
                            {loanPurpose === "Refinance"
                              ? "Proceeds:"
                              : "Cash to close:"}
                          </span>
                          <span>
                            ${formatMoney(parseInt(Math.abs(cashToClose)), 0)}
                          </span>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
              <Box
                id="pdf-9" // New unique ID for Liquidity to verify
                tabindex={0}
                sx={{
                  zIndex: 2,
                  "&:focus": {
                    outline: "none",
                    borderRadius: theme.spacing(0.5),
                    boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                      theme.palette.success.main
                    }`,
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(0.5),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          const updatedShowQuestionDescription = {
                            ...showQuestionDescription,
                            DSCR: false,
                            "Monthly cashflow": false,
                            "Cash to close": false,
                            "Liquidity to verify":
                              !showQuestionDescription["Liquidity to verify"],
                          };

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(2.8),
                            textAlign: "left",
                            color: theme.palette.primary.dark,
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(1.8),
                              textAlign: "left",
                            },
                          }}
                        >
                          Liquidity to verify
                        </Typography>
                        {showQuestionDescription["Liquidity to verify"] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>

                    <Box
                      sx={{
                        flex: "0 1 50%",
                        margin: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        minHeight: theme.spacing(4),
                        width: "100%",
                        maxWidth: "200px",
                        overflow: "hidden",
                        [theme.breakpoints.down("sm")]: {
                          maxWidth: "100%",
                        },
                      }}
                    >
                      {userProfile && Object.keys(userProfile).length === 0 ? (
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => setShowAuth(true)}
                          sx={{
                            px: 2,
                            py: 0.75,
                          }}
                        >
                          Sign in to view
                        </Button>
                      ) : (
                        <Box
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(2),
                            textAlign: "center",
                            width: "100%",
                            position: "absolute",
                            transition:
                              "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform:
                              (loanTermsUI &&
                                !userHasChangedValuesInLoanTermsUI &&
                                calculatedValueDifferences.liquidityToVerify &&
                                Math.abs(
                                  calculatedValueDifferences.liquidityToVerify
                                    .difference
                                ) > 0.01) ||
                              ((!loanTermsUI ||
                                (loanTermsUI &&
                                  userHasChangedValuesInLoanTermsUI)) &&
                                valueChanges.liquidityToVerify &&
                                Math.abs(valueChanges.liquidityToVerify) > 0.01)
                                ? `translateX(-${theme.spacing(5)})`
                                : "translateX(0)",
                            [theme.breakpoints.down("sm")]: {
                              fontSize: theme.spacing(1.6),
                            },
                          }}
                        >
                          {userProfile && Object.keys(userProfile).length === 0
                            ? ""
                            : `$${formatMoney(
                                Math.max(0, liquidityToVerify),
                                0
                              )}`}
                        </Box>
                      )}

                      {/* Always present difference indicator */}
                      <Typography
                        component="span"
                        sx={{
                          position: "absolute",
                          opacity:
                            (loanTermsUI &&
                              !userHasChangedValuesInLoanTermsUI &&
                              calculatedValueDifferences.liquidityToVerify &&
                              Math.abs(
                                calculatedValueDifferences.liquidityToVerify
                                  .difference
                              ) > 0.01) ||
                            ((!loanTermsUI ||
                              (loanTermsUI &&
                                userHasChangedValuesInLoanTermsUI)) &&
                              valueChanges.liquidityToVerify &&
                              Math.abs(valueChanges.liquidityToVerify) > 0.01)
                              ? 1
                              : 0,
                          transition:
                            "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                          transform:
                            (loanTermsUI &&
                              !userHasChangedValuesInLoanTermsUI &&
                              calculatedValueDifferences.liquidityToVerify &&
                              Math.abs(
                                calculatedValueDifferences.liquidityToVerify
                                  .difference
                              ) > 0.01) ||
                            ((!loanTermsUI ||
                              (loanTermsUI &&
                                userHasChangedValuesInLoanTermsUI)) &&
                              valueChanges.liquidityToVerify &&
                              Math.abs(valueChanges.liquidityToVerify) > 0.01)
                              ? `translateX(${theme.spacing(5)})`
                              : "translateX(0)",
                          color:
                            loanTermsUI &&
                            !userHasChangedValuesInLoanTermsUI &&
                            calculatedValueDifferences.liquidityToVerify
                              ? calculatedValueDifferences.liquidityToVerify
                                  .difference > 0
                                ? "error.main"
                                : "success.main"
                              : valueChanges.liquidityToVerify > 0
                              ? "error.main"
                              : "success.main",
                          fontSize: theme.spacing(1.8),
                          fontWeight: 600,
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(1.2),
                          },
                        }}
                      >
                        {loanTermsUI &&
                        !userHasChangedValuesInLoanTermsUI &&
                        calculatedValueDifferences.liquidityToVerify
                          ? `${
                              calculatedValueDifferences.liquidityToVerify
                                .difference > 0
                                ? "+"
                                : "-"
                            }$${formatMoney(
                              Math.abs(
                                calculatedValueDifferences.liquidityToVerify
                                  .difference
                              ),
                              0
                            )}`
                          : valueChanges.liquidityToVerify
                          ? `${
                              valueChanges.liquidityToVerify > 0 ? "+" : "-"
                            }$${formatMoney(
                              Math.abs(valueChanges.liquidityToVerify),
                              0
                            )}`
                          : "+$0"}
                      </Typography>
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription["Liquidity to verify"]}>
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.paper,
                          m: 1,
                        }}
                      >
                        {loanPurpose === "Refinance" ? (
                          <>
                            {parseInt(originationFee || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                  fontWeight: "bold",
                                }}
                              >
                                <span>+ Origination fee:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    parseInt(originationFee || 0),
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                            {parseInt(loanFees || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Loan fees:</span>
                                <span>
                                  ${formatMoney(parseInt(loanFees || 0), 0)}
                                </span>
                              </Box>
                            )}
                            {parseInt(titleFees || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Title fees:</span>
                                <span>
                                  ${formatMoney(parseInt(titleFees || 0), 0)}
                                </span>
                              </Box>
                            )}
                            {parseInt(escrow || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Escrow:</span>
                                <span>
                                  ${formatMoney(parseInt(escrow || 0), 0)}
                                </span>
                              </Box>
                            )}
                            {parseInt(prepaidInterest || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Prepaid interest:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    parseInt(prepaidInterest || 0),
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                            {parseInt(prepaidInsurance || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Prepaid insurance:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    parseInt(prepaidInsurance || 0),
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                            {parseInt(buydownAmount || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Buydown amount:</span>
                                <span>
                                  $
                                  {formatMoney(parseInt(buydownAmount || 0), 0)}
                                </span>
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span style={{ textAlign: "left" }}>
                                + PITIA reserve ({pitiaReserveMonths} months):
                              </span>
                              <span>
                                $
                                {formatMoney(
                                  (parseInt(monthlyPmt) +
                                    parseInt(annualTaxes) / 12 +
                                    parseInt(annualInsurance) / 12 +
                                    parseInt(annualHOA) / 12) *
                                    pitiaReserveMonths,
                                  0
                                )}
                              </span>
                            </Box>
                            {parseInt(cashToClose || 0) !== 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>- 50% of net proceeds:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    Math.abs(parseInt(cashToClose)) * 0.5,
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                          </>
                        ) : (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                                fontWeight: "bold",
                              }}
                            >
                              <span>Downpayment:</span>
                              <span>
                                $
                                {formatMoney(
                                  Math.abs(
                                    parseInt(variableExpenseCashToClose) -
                                      parseInt(loanAmount)
                                  ),
                                  0
                                )}
                              </span>
                            </Box>
                            {parseInt(originationFee || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Origination fee:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    parseInt(originationFee || 0),
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                            {parseInt(loanFees || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Loan fees:</span>
                                <span>
                                  ${formatMoney(parseInt(loanFees || 0), 0)}
                                </span>
                              </Box>
                            )}
                            {parseInt(titleFees || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Title fees:</span>
                                <span>
                                  ${formatMoney(parseInt(titleFees || 0), 0)}
                                </span>
                              </Box>
                            )}
                            {parseInt(escrow || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Escrow:</span>
                                <span>
                                  ${formatMoney(parseInt(escrow || 0), 0)}
                                </span>
                              </Box>
                            )}
                            {parseInt(prepaidInterest || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Prepaid interest:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    parseInt(prepaidInterest || 0),
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                            {parseInt(prepaidInsurance || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Prepaid insurance:</span>
                                <span>
                                  $
                                  {formatMoney(
                                    parseInt(prepaidInsurance || 0),
                                    0
                                  )}
                                </span>
                              </Box>
                            )}
                            {parseInt(buydownAmount || 0) > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <span>+ Buydown amount:</span>
                                <span>
                                  $
                                  {formatMoney(parseInt(buydownAmount || 0), 0)}
                                </span>
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.5,
                              }}
                            >
                              <span style={{ textAlign: "left" }}>
                                + PITIA reserve ({pitiaReserveMonths} months):
                              </span>
                              <span>
                                $
                                {formatMoney(
                                  (parseInt(monthlyPmt) +
                                    parseInt(annualTaxes) / 12 +
                                    parseInt(annualInsurance) / 12 +
                                    parseInt(annualHOA) / 12) *
                                    pitiaReserveMonths,
                                  0
                                )}
                              </span>
                            </Box>
                          </>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 1,
                            pt: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            fontWeight: "bold",
                          }}
                        >
                          <span>Liquidity to verify:</span>
                          <span>
                            ${formatMoney(Math.abs(liquidityToVerify), 0)}
                          </span>
                        </Box>
                        <Box
                          sx={{
                            marginTop: theme.spacing(2),
                            paddingTop: theme.spacing(2),
                            borderTop: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          {(() => {
                            const liquidityQuestion = answers.find(
                              (answer) =>
                                answer.q_title === "Liquidity to verify"
                            );
                            return liquidityQuestion?.q_description ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: theme.spacing(1.6),
                                  color: theme.palette.text.secondary,
                                  fontStyle: "italic",
                                }}
                              >
                                {liquidityQuestion.q_description(answers)}
                              </Typography>
                            ) : null;
                          })()}
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
              {/* Shared login prompt (logged-out mode) */}
              {userProfile && Object.keys(userProfile).length === 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                  <Dialog
                    open={showAuth}
                    onClose={() => setShowAuth(false)}
                    maxWidth="sm"
                    fullWidth
                  >
                    <DialogContent>
                      <Authenticator
                        customTitle={"Log in or sign up to continue"}
                      />
                    </DialogContent>
                  </Dialog>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                margin: theme.spacing(2, 0, 0, 0),

                position: "relative",
                overflow: "hidden",
                [theme.breakpoints.down("md")]: {
                  margin: theme.spacing(1, 0, 2, 0),
                  padding: theme.spacing(2),
                },
              }}
            >
              {!loanTermsUI &&
                showQuestionDescription[`Cash flow and cash to close`] && (
                  <Button
                    sx={{ width: { xs: "100%", md: "auto" }, margin: "auto" }}
                    variant="outlined" // Changed to outlined for consistency
                    color="primary"
                    fullWidth // Make button full width
                    onClick={() =>
                      setShowClosingCostsCollapse(!showClosingCostsCollapse)
                    }
                    endIcon={
                      showClosingCostsCollapse ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )
                    }
                  >
                    {showClosingCostsCollapse
                      ? "Hide closing costs"
                      : "Show closing costs"}
                  </Button>
                )}

              <Collapse in={showClosingCostsCollapse}>
                <Box
                  id="pdf-6.5"
                  sx={{
                    m: { xs: 0.25, md: 1 },
                  }}
                >
                  {" "}
                  {/* Closing costs content */}
                  <QuestionRenderer
                    answers={answers}
                    questions={secondPartOfQuestions}
                    theme={theme}
                    showQuestionDescription={showQuestionDescription}
                    setShowQuestionDescription={setShowQuestionDescription}
                    generateAnswerOptions={generateAnswerOptions}
                    descriptionParams={loanTermsUI && loan ? loan : {}}
                    customContent={{
                      "Origination fee": <Box>{originationFeeExplanation}</Box>,
                      // "Loan fees": <Box>{originationFeeExplanation}</Box>,
                    }}
                  />
                </Box>
              </Collapse>
            </Box>{" "}
          </Box>
          <Box
            sx={{
              m: loanTermsUI
                ? {
                    xs: "0 2%", // Mobile: smaller margins
                    sm: "0 3%", // Tablet: medium margins
                    md: "0 5%", // Desktop: standard margins
                  }
                : 0,
            }}
            id="pdf-10"
          >
            <SaveSharePDF
              answers={answers}
              allowedQuestionTitles={thirdPart}
              generateAnswerOptions={generateAnswerOptions}
              calc={calc}
              setCalc={setCalc}
              calculatorType={calcType}
              loanTermsUI={loanTermsUI}
              loan={loan}
              onVersionSelect={onVersionSelect}
            />
          </Box>
          {!loanTermsUI && dscr > 1.1 && (
            <Alert
              sx={{
                margin: theme.spacing(10, 0),
                width: "100%",
                "& .MuiAlert-message": {
                  width: "100%",
                },
                "& .MuiButtonBase-root": {
                  backgroundColor: `${theme.palette.success.main}`,
                },
              }}
              severity="success"
              variant={"outlined"}
            >
              <>
                <b>Looks like a great fit for our DSCR program.</b>
                <br />
                <br />
                Speak with a DSCR loan expert when you get an instant quote
              </>

              <Box
                sx={{
                  margin: theme.spacing(1, "auto", 0, "auto"),
                  display: "flex",
                  flexDirection: "column",
                  width: "33%",
                  [theme.breakpoints.down("md")]: {
                    width: "100%",
                  },
                }}
              >
                <Button
                  color="success"
                  sx={{
                    textDecoration: "none !important",
                    color: "white !important",
                    margin: theme.spacing(1.5, 0, 0, 0),
                    "&:hover": {
                      color: `${theme.palette.primary.contrastColor} !important`,
                      backgroundColor: `${theme.palette.success.dark} !important`,
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "auto",
                    },
                  }}
                  href={"/loans?ref=dscr-calculator-btn"}
                  onClick={() => {
                    gtag.event({ action: "dscr_calc_to_quoter_navigation" });
                  }}
                >
                  <Box
                    sx={{
                      margin: theme.spacing(0, 1),
                      fontSize: theme.spacing(2),
                      padding: theme.spacing(0.5),
                    }}
                  >
                    {" "}
                    ⚡
                  </Box>
                  Instant quote{" "}
                </Button>
                <Typography
                  component="h2"
                  sx={{
                    display: "flex",
                    fontSize: theme.spacing(1.5),
                    margin: theme.spacing(0, 0, 0, "auto"),
                    [theme.breakpoints.down("md")]: {
                      display: "block",
                      fontSize: theme.spacing(1.2),
                    },
                  }}
                >
                  *takes 1 minute, no credit pull
                </Typography>
              </Box>
            </Alert>
          )}
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
    </Box>
  );
}
